import { buildJacketFrontButtons, normalizeJacketButtonLayout, normalizeJacketFrontClosure } from './buttonPlan';

export const JACKET_V0_FORMULA_VERSION = 'jacket_siy_v0';
export const JACKET_ACTIVE_FORMULA_VERSION = JACKET_V0_FORMULA_VERSION;

const n = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const roundToEighth = (value) => Math.round(value / 0.125) * 0.125;
const roundToSixteenth = (value) => Math.round(value / 0.0625) * 0.0625;

const rp = (x, y) => ({
  x: Number(x.toFixed(4)),
  y: Number(y.toFixed(4)),
});

const fmt = (value) => Number(value).toFixed(4).replace(/\.0+$/, '').replace(/(\.\d*?)0+$/, '$1');
const toSVG = (point) => `${fmt(point.x)} ${fmt(point.y)}`;
const toCanvasPoint = (point) => rp(point.x, -point.y);

const reflectPointAcrossLine = (point, lineStart, lineEnd) => {
  const vx = lineEnd.x - lineStart.x;
  const vy = lineEnd.y - lineStart.y;
  const denom = (vx * vx) + (vy * vy);
  if (denom < 1e-8) return rp(point.x, point.y);

  const t = (((point.x - lineStart.x) * vx) + ((point.y - lineStart.y) * vy)) / denom;
  const proj = {
    x: lineStart.x + (vx * t),
    y: lineStart.y + (vy * t),
  };

  return rp((2 * proj.x) - point.x, (2 * proj.y) - point.y);
};

const midpoint = (a, b, dx = 0, dy = 0) => rp((a.x + b.x) / 2 + dx, (a.y + b.y) / 2 + dy);

const normalize = (vector, fallback = { x: 1, y: 0 }) => {
  const mag = Math.hypot(vector.x, vector.y);
  if (mag < 1e-8) return fallback;
  return { x: vector.x / mag, y: vector.y / mag };
};

const dist = (a, b) => Math.hypot((b.x - a.x), (b.y - a.y));

const polylineLength = (points = []) => points.slice(1).reduce(
  (sum, point, index) => sum + dist(points[index], point),
  0,
);

const pointOnLineAtY = (start, end, targetY) => {
  const dy = end.y - start.y;
  if (Math.abs(dy) < 1e-8) return rp(start.x, targetY);
  const t = (targetY - start.y) / dy;
  return rp(start.x + ((end.x - start.x) * t), targetY);
};

const offsetPointTowardInterior = (basePoint, lineStart, lineEnd, offsetDistance, insideHint) => {
  const tangent = normalize({ x: lineEnd.x - lineStart.x, y: lineEnd.y - lineStart.y }, { x: 1, y: 0 });
  const normalA = { x: -tangent.y, y: tangent.x };
  const normalB = { x: tangent.y, y: -tangent.x };
  const candidateA = rp(basePoint.x + (normalA.x * offsetDistance), basePoint.y + (normalA.y * offsetDistance));
  const candidateB = rp(basePoint.x + (normalB.x * offsetDistance), basePoint.y + (normalB.y * offsetDistance));

  if (!insideHint) return candidateA;

  const distanceToHint = (point) => Math.hypot(point.x - insideHint.x, point.y - insideHint.y);
  return distanceToHint(candidateA) <= distanceToHint(candidateB) ? candidateA : candidateB;
};

function quadPoint(p0, p1, p2, t) {
  const oneMinusT = 1 - t;
  return {
    x: (oneMinusT * oneMinusT * p0.x) + (2 * oneMinusT * t * p1.x) + (t * t * p2.x),
    y: (oneMinusT * oneMinusT * p0.y) + (2 * oneMinusT * t * p1.y) + (t * t * p2.y),
  };
}

function quadLength(p0, p1, p2, samples = 120) {
  let length = 0;
  let prev = p0;
  for (let i = 1; i <= samples; i += 1) {
    const t = i / samples;
    const point = quadPoint(p0, p1, p2, t);
    length += Math.hypot(point.x - prev.x, point.y - prev.y);
    prev = point;
  }
  return length;
}

const dot = (a, b) => (a.x * b.x) + (a.y * b.y);

const lerpPoint = (a, b, t) => rp(
  a.x + ((b.x - a.x) * t),
  a.y + ((b.y - a.y) * t),
);

const splitQuadraticAtT = (p0, p1, p2, t) => {
  const q0 = lerpPoint(p0, p1, t);
  const q1 = lerpPoint(p1, p2, t);
  const r = lerpPoint(q0, q1, t);
  return {
    left: { p0, p1: q0, p2: r },
    right: { p0: r, p1: q1, p2 },
  };
};

const findQuadraticSegmentFromEndByLength = (p0, p1, p2, targetLength) => {
  const totalLength = quadLength(p0, p1, p2);
  if (targetLength <= 0) {
    return { t: 1, segment: { p0: p2, p1: p2, p2 }, point: p2, length: 0 };
  }
  if (targetLength >= totalLength) {
    return { t: 0, segment: { p0, p1, p2 }, point: p0, length: totalLength };
  }

  let low = 0;
  let high = 1;
  let best = { t: 0, segment: { p0, p1, p2 }, point: p0, length: totalLength };

  for (let i = 0; i < 28; i += 1) {
    const mid = (low + high) / 2;
    const split = splitQuadraticAtT(p0, p1, p2, mid);
    const rightLength = quadLength(split.right.p0, split.right.p1, split.right.p2);
    best = {
      t: mid,
      segment: split.right,
      point: split.right.p0,
      length: rightLength,
    };

    if (rightLength > targetLength) {
      low = mid;
    } else {
      high = mid;
    }
  }

  return best;
};

const choosePerpendicularTowardPoint = (direction, fromPoint, towardPoint) => {
  const normalA = normalize({ x: -direction.y, y: direction.x }, { x: 0, y: -1 });
  const normalB = { x: -normalA.x, y: -normalA.y };
  const towardVector = { x: towardPoint.x - fromPoint.x, y: towardPoint.y - fromPoint.y };
  return dot(normalA, towardVector) >= dot(normalB, towardVector) ? normalA : normalB;
};

const normalizeBellyProfile = (value = 'normal') => {
  const normalized = String(value || 'normal').toLowerCase();
  if (['slim', 'normal', 'slight_belly', 'big_belly', 'extreme_belly'].includes(normalized)) {
    return normalized;
  }
  return 'normal';
};

const normalizeJacketDartMode = (value = 'belly_and_fish') => {
  const normalized = String(value || 'belly_and_fish').toLowerCase();
  if (['off', 'fish', 'belly_and_fish'].includes(normalized)) {
    return normalized;
  }
  return 'belly_and_fish';
};

const getBellyProfileRule = (bellyProfile = 'normal') => {
  const key = normalizeBellyProfile(bellyProfile);
  if (key === 'slim') return { frontSideOffset: 1.25, cfForwardOffset: 0, swpfOffsetFromScpf: -0.5 };
  if (key === 'slight_belly') return { frontSideOffset: 0.75, cfForwardOffset: 0.25, swpfOffsetFromScpf: 0 };
  if (key === 'big_belly') return { frontSideOffset: 0.5, cfForwardOffset: 0.5, swpfOffsetFromScpf: 0.25 };
  if (key === 'extreme_belly') return { frontSideOffset: 0.375, cfForwardOffset: 1, swpfOffsetFromScpf: 0.5 };
  return { frontSideOffset: 1, cfForwardOffset: 0, swpfOffsetFromScpf: -0.25 };
};

const solveSideHemYForTargetSeamLength = ({ startPoint, control1, control2, control3, targetEnd, targetLength, x }) => {
  if (!Number.isFinite(targetLength) || targetLength <= 0) {
    return targetEnd.y;
  }

  let low = startPoint.y;
  let high = Math.max(targetEnd.y + 12, startPoint.y + 12);
  let bestY = targetEnd.y;

  for (let i = 0; i < 32; i += 1) {
    const mid = (low + high) / 2;
    const candidateEnd = rp(x, mid);
    const length = cubicLength(startPoint, control1, control2, control3, candidateEnd);
    bestY = mid;
    if (length < targetLength) {
      low = mid;
    } else {
      high = mid;
    }
  }

  return bestY;
};

const catmullRomToBezier = (p0, p1, p2, p3, tension = 1) => {
  const t = tension / 6;
  return {
    c1: rp(p1.x + (p2.x - p0.x) * t, p1.y + (p2.y - p0.y) * t),
    c2: rp(p2.x - (p3.x - p1.x) * t, p2.y - (p3.y - p1.y) * t),
  };
};

const cubicPoint = (p0, p1, p2, p3, t) => {
  const oneMinusT = 1 - t;
  return rp(
    (oneMinusT ** 3 * p0.x)
      + (3 * (oneMinusT ** 2) * t * p1.x)
      + (3 * oneMinusT * (t ** 2) * p2.x)
      + ((t ** 3) * p3.x),
    (oneMinusT ** 3 * p0.y)
      + (3 * (oneMinusT ** 2) * t * p1.y)
      + (3 * oneMinusT * (t ** 2) * p2.y)
      + ((t ** 3) * p3.y),
  );
};

const cubicLength = (p0, p1, p2, p3, samples = 120) => {
  let length = 0;
  let prev = p0;
  for (let i = 1; i <= samples; i += 1) {
    const t = i / samples;
    const point = cubicPoint(p0, p1, p2, p3, t);
    length += Math.hypot(point.x - prev.x, point.y - prev.y);
    prev = point;
  }
  return length;
};

const buildPolylinePath = (points = []) => {
  if (!Array.isArray(points) || points.length < 2) return '';
  return points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${toSVG(point)}`).join(' ');
};

const sampleCubicTailByLength = (p0, p1, p2, p3, targetLength, samples = 180) => {
  const sampledPoints = [];
  for (let i = 0; i <= samples; i += 1) {
    sampledPoints.push(cubicPoint(p0, p1, p2, p3, i / samples));
  }

  const cumulative = [0];
  for (let i = 1; i < sampledPoints.length; i += 1) {
    cumulative[i] = cumulative[i - 1] + dist(sampledPoints[i - 1], sampledPoints[i]);
  }

  const totalLength = cumulative[cumulative.length - 1] || 0;
  if (targetLength <= 0) return { points: [sampledPoints[sampledPoints.length - 1]], length: 0 };
  if (targetLength >= totalLength) return { points: sampledPoints, length: totalLength };

  const startDistance = totalLength - targetLength;
  let startIndex = 1;
  while (startIndex < cumulative.length && cumulative[startIndex] < startDistance) {
    startIndex += 1;
  }

  const prevDistance = cumulative[startIndex - 1] || 0;
  const nextDistance = cumulative[startIndex] || prevDistance;
  const segmentSpan = Math.max(1e-8, nextDistance - prevDistance);
  const ratio = (startDistance - prevDistance) / segmentSpan;
  const prevPoint = sampledPoints[startIndex - 1];
  const nextPoint = sampledPoints[startIndex];
  const startPoint = lerpPoint(prevPoint, nextPoint, ratio);

  return {
    points: [startPoint, ...sampledPoints.slice(startIndex)],
    length: targetLength,
  };
};

const SHAWL_FAMILY_LAPEL_TYPES = new Set(['shawl', 'diamond_shawl']);

const isShawlFamilyLapelType = (lapelType) => SHAWL_FAMILY_LAPEL_TYPES.has(lapelType);

const createDiamondShawlLapelPath = ({
  FHPS,
  UCOL1,
  UCOL2,
  TCOL1,
  TCOL2,
  RL1,
  RL2,
  curveCtrl,
}) => {
  const upperStraightLength = dist(FHPS, UCOL1)
    + dist(UCOL1, UCOL2)
    + dist(UCOL2, TCOL1)
    + dist(TCOL1, TCOL2);
  const diamondQuadraticLength = quadLength(TCOL2, curveCtrl, RL2);
  const diamondSegmentLength = dist(RL2, RL1);
  const outerCurveLength = diamondQuadraticLength + diamondSegmentLength;

  return {
    path: `M ${toSVG(FHPS)} L ${toSVG(UCOL1)} L ${toSVG(UCOL2)} L ${toSVG(TCOL1)} L ${toSVG(TCOL2)} Q ${toSVG(curveCtrl)} ${toSVG(RL2)} L ${toSVG(RL1)}`,
    lines: [
      { id: 'lapel-shawl-fhps-ucol1', p1: FHPS, p2: UCOL1, label: 'FHPS→UCOL1' },
      { id: 'lapel-shawl-ucol1-ucol2', p1: UCOL1, p2: UCOL2, label: 'UCOL1→UCOL2' },
      { id: 'lapel-shawl-ucol2-tcol1', p1: UCOL2, p2: TCOL1, label: 'UCOL2→TCOL1' },
      { id: 'lapel-shawl-tcol1-tcol2', p1: TCOL1, p2: TCOL2, label: 'TCOL1→TCOL2' },
      { id: 'lapel-diamond-shawl-tcol2-rl2', p1: TCOL2, p2: RL2, label: 'TCOL2→RL2' },
      { id: 'lapel-diamond-shawl-rl2-rl1', p1: RL2, p2: RL1, label: 'RL2→RL1' },
    ],
    metrics: {
      upperStraightLength: Number(upperStraightLength.toFixed(3)),
      diamondQuadraticLength: Number(diamondQuadraticLength.toFixed(3)),
      diamondSegmentLength: Number(diamondSegmentLength.toFixed(3)),
      outerCurveLength: Number(outerCurveLength.toFixed(3)),
      crownCurveLength: Number(outerCurveLength.toFixed(3)),
      fallCurveLength: Number(outerCurveLength.toFixed(3)),
    },
    formulaLines: [
      'Diamond Shawl Lapel:',
      'The Front cut contour runs FHPS→UCOL1→UCOL2→TCOL1→TCOL2, then a quadratic curve to RL2 and a straight segment RL2→RL1.',
      'FHPS→UCOL1→UCOL2→TCOL1→TCOL2 uses straight segments like the standard shawl.',
      'RL2 seeds at RL1.x + 3" and RL1.y - 1" (+ jacketRl2Dx/Dy and Free Mode on RL2).',
      'TCOL2→RL2 uses one quadratic control from the TCOL2→RL2 span (diamondShawlTcol2Rl2CtrlDx/Dy, default 0,0).',
      'The TCOL2→RL2→RL1 chain is reflected across the roll line for the folded-body preview.',
    ],
  };
};

const createShawlLapelPath = ({
  FHPS,
  UCOL1,
  UCOL2,
  TCOL1,
  TCOL2,
  LP0_M,
  RL1,
  curveCtrl1,
  curveCtrl2,
}) => {
  const upperStraightLength = dist(FHPS, UCOL1)
    + dist(UCOL1, UCOL2)
    + dist(UCOL2, TCOL1)
    + dist(TCOL1, TCOL2);
  const outerCurveLength = cubicLength(TCOL2, curveCtrl1, curveCtrl2, RL1);

  return {
    path: `M ${toSVG(FHPS)} L ${toSVG(UCOL1)} L ${toSVG(UCOL2)} L ${toSVG(TCOL1)} L ${toSVG(TCOL2)} C ${toSVG(curveCtrl1)} ${toSVG(curveCtrl2)} ${toSVG(RL1)}`,
    lines: [
      { id: 'lapel-shawl-fhps-ucol1', p1: FHPS, p2: UCOL1, label: 'FHPS→UCOL1' },
      { id: 'lapel-shawl-ucol1-ucol2', p1: UCOL1, p2: UCOL2, label: 'UCOL1→UCOL2' },
      { id: 'lapel-shawl-ucol2-tcol1', p1: UCOL2, p2: TCOL1, label: 'UCOL2→TCOL1' },
      { id: 'lapel-shawl-tcol1-tcol2', p1: TCOL1, p2: TCOL2, label: 'TCOL1→TCOL2' },
      { id: 'lapel-shawl-tcol2-rl1', p1: TCOL2, p2: RL1, label: 'TCOL2→RL1' },
    ],
    metrics: {
      upperStraightLength: Number(upperStraightLength.toFixed(3)),
      crownCurveLength: Number(outerCurveLength.toFixed(3)),
      fallCurveLength: Number(outerCurveLength.toFixed(3)),
      outerCurveLength: Number(outerCurveLength.toFixed(3)),
    },
    formulaLines: [
      'Shawl Lapel:',
      'The Front cut contour runs FHPS→UCOL1→UCOL2→TCOL1→TCOL2, then one cubic curve connects directly to RL1.',
      'FHPS→UCOL1 leaves FHPS at 90° to the shoulder seam, and that straight segment equals the exact back-neck curve length.',
      'FHPS→UCOL1→UCOL2→TCOL1→TCOL2 uses straight segments.',
      'The TCOL2→RL1 cubic uses two independent controls positioned from the TCOL2→LP0_M and LP0_M→RL1 spans. LP0_M remains a visible shaping reference and is not a required on-curve anchor.',
      'The full TCOL2→RL1 cubic is reflected across the roll line for the folded-body preview.',
      'FND and LP1 remain internal roll/fold construction references. GP, CP0, GP_M, CP0_M, LB0, LB1, LB0_M, and LB1_M are omitted.',
    ],
  };
};

const createPeakLapelPath = ({ FND, LP2, LP3, RL1, GP, CP0, LB0, LB1, LP2_M, LP3_M, RL1_M, GP_M, CP0_M, LB0_M, LB1_M, fallCtrl, fallCtrlMirror }) => ({
  path: `M ${toSVG(FND)} L ${toSVG(LP2)} L ${toSVG(LP3)} Q ${toSVG(fallCtrl)} ${toSVG(RL1)} M ${toSVG(GP)} L ${toSVG(CP0)} M ${toSVG(LB0)} L ${toSVG(LB1)} M ${toSVG(FND)} L ${toSVG(LP2_M)} L ${toSVG(LP3_M)} Q ${toSVG(fallCtrlMirror)} ${toSVG(RL1_M)} M ${toSVG(GP_M)} L ${toSVG(CP0_M)} M ${toSVG(LB0_M)} L ${toSVG(LB1_M)}`,
  lines: [
    { id: 'lapel-peak-fnd-lp2', p1: FND, p2: LP2, label: 'FND→LP2' },
    { id: 'lapel-peak-lp2-lp3', p1: LP2, p2: LP3, label: 'LP2→LP3' },
    { id: 'lapel-peak-lp3-rl1', p1: LP3, p2: RL1, label: 'LP3→RL1' },
    { id: 'lapel-gp-cp0', p1: GP, p2: CP0, label: 'GP→CP0' },
    { id: 'lapel-lb0-lb1', p1: LB0, p2: LB1, label: 'LB0→LB1' },
    { id: 'lapel-peak-fnd-lp2-mirror', p1: FND, p2: LP2_M, label: 'Mirror FND→LP2' },
    { id: 'lapel-peak-lp2-lp3-mirror', p1: LP2_M, p2: LP3_M, label: 'Mirror LP2→LP3' },
    { id: 'lapel-peak-lp3-rl1-mirror', p1: LP3_M, p2: RL1_M, label: 'Mirror LP3→RL1' },
    { id: 'lapel-gp-cp0-mirror', p1: GP_M, p2: CP0_M, label: 'Mirror GP→CP0' },
    { id: 'lapel-lb0-lb1-mirror', p1: LB0_M, p2: LB1_M, label: 'Mirror LB0→LB1' },
  ],
  metrics: {
    peakRise: Number(Math.abs(LP2.y - LP3.y).toFixed(3)),
    peakTipDistance: Number(dist(LP2, LP3).toFixed(3)),
    fallCurveLength: Number(quadLength(LP3, fallCtrl, RL1).toFixed(3)),
  },
  formulaLines: [
    'Peak Lapel:',
    'LP2 is the midpoint of LP0→LP1, and FND connects directly to LP2.',
    'RL1→LP3 is extended through LP0, with LP3 fixed 1" above LP2 in drafting coordinates.',
    'Peak contour = FND→LP2→LP3→RL1, with LP3→RL1 controlled by an editable fall-curve handle and then reflected across the roll line.',
    'GP coincides with LP2. CP0 sits 0.5" from LP3 on segment LP3→LP2 and reflects to CP0_M for the collar join.',
  ],
});

const createRoundedNotchLapelPath = ({
  FND,
  LP0,
  RN1,
  RN2,
  RL1,
  GP,
  CP0,
  LB0,
  LB1,
  RN1_M,
  RN2_M,
  RL1_M,
  GP_M,
  LB0_M,
  LB1_M,
  roundCtrl,
  roundCtrlMirror,
  fallCtrl,
  fallCtrlMirror,
  collarEntry,
  collarExit,
  collarRoundCtrl,
}) => {
  const roundedCurveLength = quadLength(RN1, roundCtrl, RN2);
  const fallCurveLength = quadLength(RN2, fallCtrl, RL1);
  const collarRoundLength = collarEntry && collarExit && collarRoundCtrl
    ? quadLength(collarEntry, collarRoundCtrl, collarExit)
    : 0;

  return {
    path: `M ${toSVG(FND)} L ${toSVG(RN1)} Q ${toSVG(roundCtrl)} ${toSVG(RN2)} Q ${toSVG(fallCtrl)} ${toSVG(RL1)} M ${toSVG(LB0)} L ${toSVG(LB1)} M ${toSVG(FND)} L ${toSVG(RN1_M)} Q ${toSVG(roundCtrlMirror)} ${toSVG(RN2_M)} Q ${toSVG(fallCtrlMirror)} ${toSVG(RL1_M)} M ${toSVG(LB0_M)} L ${toSVG(LB1_M)}`,
    lines: [
      { id: 'lapel-rounded-fnd-rn1', p1: FND, p2: RN1, label: 'FND→RN1' },
      { id: 'lapel-rounded-rn1-rn2', p1: RN1, p2: RN2, label: 'RN1→RN2' },
      { id: 'lapel-rounded-rn2-rl1', p1: RN2, p2: RL1, label: 'RN2→RL1' },
      { id: 'lapel-lb0-lb1', p1: LB0, p2: LB1, label: 'LB0→LB1' },
      { id: 'lapel-rounded-fnd-rn1-mirror', p1: FND, p2: RN1_M, label: 'Mirror FND→RN1' },
      { id: 'lapel-rounded-rn1-rn2-mirror', p1: RN1_M, p2: RN2_M, label: 'Mirror RN1→RN2' },
      { id: 'lapel-rounded-rn2-rl1-mirror', p1: RN2_M, p2: RL1_M, label: 'Mirror RN2→RL1' },
      { id: 'lapel-lb0-lb1-mirror', p1: LB0_M, p2: LB1_M, label: 'Mirror LB0→LB1' },
    ],
    metrics: {
      roundedCurveLength: Number(roundedCurveLength.toFixed(3)),
      fallCurveLength: Number(fallCurveLength.toFixed(3)),
      collarRoundLength: Number(collarRoundLength.toFixed(3)),
      roundDepth: Number(dist(LP0, roundCtrl).toFixed(3)),
    },
    formulaLines: [
      'Rounded Notch Lapel:',
      'RN1 is positioned 0.75" from LP0 on LP0→FND, and RN2 is positioned 0.75" from LP0 on LP0→RL1.',
      'LP0 stays visible as a reference only; the live notch corner is a dedicated RN1→RN2 quadratic with its own control handle.',
      'The lapel fall control re-anchors from RN2→RL1, and the mirrored collar corner uses CP2_M on CP0_M→TCOL2 plus CP1_M on CP0_M→GP_M. TCOL2 approaches CP2_M under the top-collar controls, while the local rounded corner CP2_M→CP1_M has its own independent round-control handle before the straight GP_M tail. CP0_M stays an internal reference only.',
    ],
  };
};

function smoothTurnPoint({ prevPoint, turnPoint, nextPoint, incomingControl, outgoingControl }) {
  const inLen = Math.max(0.125, dist(turnPoint, incomingControl));
  const outLen = Math.max(0.125, dist(turnPoint, outgoingControl));

  const inTan = normalize({ x: turnPoint.x - incomingControl.x, y: turnPoint.y - incomingControl.y }, normalize({ x: turnPoint.x - prevPoint.x, y: turnPoint.y - prevPoint.y }));
  const outTan = normalize({ x: outgoingControl.x - turnPoint.x, y: outgoingControl.y - turnPoint.y }, normalize({ x: nextPoint.x - turnPoint.x, y: nextPoint.y - turnPoint.y }));
  const blend = normalize({ x: inTan.x + outTan.x, y: inTan.y + outTan.y }, normalize({ x: nextPoint.x - prevPoint.x, y: nextPoint.y - prevPoint.y }));

  return {
    incomingControl: rp(turnPoint.x - blend.x * inLen, turnPoint.y - blend.y * inLen),
    outgoingControl: rp(turnPoint.x + blend.x * outLen, turnPoint.y + blend.y * outLen),
  };
}

const JACKET_KEY_POINT_HANDLES = [
  { id: 'jacket-point-cb4', name: 'CB4', label: 'CB4', piece: 'back', xKey: 'jacketCb4Dx', yKey: 'jacketCb4Dy' },
  { id: 'jacket-point-blps', name: 'BLPS', label: 'BLPS', piece: 'back', xKey: 'jacketBlpsDx', yKey: 'jacketBlpsDy' },
  { id: 'jacket-point-bxb', name: 'BXB', label: 'BXB', piece: 'back', xKey: 'jacketBxbDx', yKey: 'jacketBxbDy' },
  { id: 'jacket-point-flps', name: 'FLPS', label: 'FLPS', piece: 'front', xKey: 'jacketFlpsDx', yKey: 'jacketFlpsDy' },
  { id: 'jacket-point-fxf', name: 'FXF', label: 'FXF', piece: 'front', xKey: 'jacketFxfDx', yKey: 'jacketFxfDy' },
  { id: 'jacket-point-bwps', name: 'BWPS', label: 'BWPS', piece: 'back', xKey: 'jacketBwpsDx', yKey: 'jacketBwpsDy' },
  { id: 'jacket-point-fwps', name: 'FWPS', label: 'FWPS', piece: 'front', xKey: 'jacketFwpsDx', yKey: 'jacketFwpsDy' },
  { id: 'jacket-point-bbps', name: 'BBPS', label: 'BBPS', piece: 'back', xKey: 'jacketBbpsDx', yKey: 'jacketBbpsDy' },
  { id: 'jacket-point-fbps', name: 'FBPS', label: 'FBPS', piece: 'front', xKey: 'jacketFbpsDx', yKey: 'jacketFbpsDy' },
  { id: 'jacket-point-bsps', name: 'BSPS', label: 'BSPS', piece: 'back', xKey: 'jacketBspsDx', yKey: 'jacketBspsDy' },
  { id: 'jacket-point-fsps', name: 'FSPS', label: 'FSPS', piece: 'front', xKey: 'jacketFspsDx', yKey: 'jacketFspsDy' },
  { id: 'jacket-point-rl1', name: 'RL1', label: 'RL1', piece: 'front', xKey: 'jacketRl1Dx', yKey: 'jacketRl1Dy' },
  { id: 'jacket-point-lp1', name: 'LP1', label: 'LP1', piece: 'front', xKey: 'jacketLp1Dx', yKey: 'jacketLp1Dy' },
  { id: 'jacket-point-lp0', name: 'LP0', label: 'LP0', piece: 'front', xKey: 'jacketLp0Dx', yKey: 'jacketLp0Dy' },
];

function applyPointHandleOffsets(basePoints, measurements) {
  const points = { ...basePoints };

  const pointHandles = JACKET_KEY_POINT_HANDLES.filter((def) => points[def.name]).map((def) => {
    const basePoint = points[def.name];
    const dx = n(measurements[def.xKey], 0);
    const dy = n(measurements[def.yKey], 0);
    const point = rp(basePoint.x + dx, basePoint.y + dy);
    points[def.name] = point;
    return {
      ...def,
      point,
      dx,
      dy,
    };
  });

  return { points, pointHandles };
}

function normalizePointDeltaMap(rawPointDeltas = {}) {
  const source = (rawPointDeltas && typeof rawPointDeltas === 'object' && rawPointDeltas.deltas && typeof rawPointDeltas.deltas === 'object')
    ? rawPointDeltas.deltas
    : rawPointDeltas;

  if (!source || typeof source !== 'object') return {};

  return Object.entries(source).reduce((acc, [pointName, delta]) => {
    if (!pointName || !delta || typeof delta !== 'object') return acc;
    const dx = roundToSixteenth(n(delta.x, 0));
    const dy = roundToSixteenth(n(delta.y, 0));
    if (Math.abs(dx) < 1e-8 && Math.abs(dy) < 1e-8) return acc;
    acc[pointName] = { x: dx, y: dy };
    return acc;
  }, {});
}

export function generateJacketPatternSIyV0(measurements = {}, controls = {}) {
  const freeModeActive = Boolean(controls?.freeModeActive);
  const freePointDeltas = normalizePointDeltaMap(controls?.pointDeltas || {});
  const hasPointDeltas = Object.keys(freePointDeltas).length > 0;

  const applyResolvedPointDeltaToPoint = (pointName, point) => {
    if (!hasPointDeltas || !pointName || !point) return;
    const delta = freePointDeltas[pointName];
    if (!delta) return;
    point.x = Number((point.x + n(delta.x, 0)).toFixed(4));
    point.y = Number((point.y + n(delta.y, 0)).toFixed(4));
  };

  const neck = clamp(n(measurements.neck, 15), 11, 22);
  const chest = clamp(n(measurements.chest, 40), 28, 60);
  const waist = clamp(n(measurements.waist, 36), 22, 56);
  const belly = clamp(n(measurements.belly, 38), 24, 58);
  const seat = clamp(n(measurements.seat, 40), 28, 60);
  const shoulder = clamp(n(measurements.shoulder, 18), 12, 24);
  const armholeScyeDepth = clamp(n(measurements.armholeScyeDepth, 9), 6.5, 14);
  const acrossBack = clamp(n(measurements.acrossBack, 17), 12, 24);
  const acrossFront = clamp(n(measurements.acrossFront, 16), 11, 24);

  const jacketLengthBack = clamp(n(measurements.jacketLengthBack, 30), 22, 46);
  const jacketLengthFront = clamp(n(measurements.jacketLengthFront, jacketLengthBack), 22, 46);
  const bellyProfile = normalizeBellyProfile(measurements?.bellyProfile || 'normal');
  const bellyProfileRule = getBellyProfileRule(bellyProfile);

  const jacketFrontClosure = normalizeJacketFrontClosure(measurements?.jacketFrontClosure);
  const isDoubleBreasted = jacketFrontClosure === 'double_breasted';

  const chestHalf = chest / 2;
  const waistHalf = waist / 2;
  const bellyHalf = belly / 2;
  const seatHalf = seat / 2;

  const originX = 0;
  const originY = 0;

  const cbX = originX;
  const cb4XBase = cbX + 0.625;
  let cfX = cb4XBase + chestHalf + 0.25;
  const bhpsY = 0;

  const autoBackNeckDepth = 0.75;
  const autoFrontNeckDepth = 3;
  const autoBackNeckWidth = roundToSixteenth(Math.max(2.5, neck / 5));
  const autoFrontNeckWidth = roundToSixteenth(Math.max(2.5, (neck / 5) + 1));

  const backNeckDepth = measurements.overrideBNDEnabled
    ? n(measurements.overrideBND, autoBackNeckDepth)
    : autoBackNeckDepth;
  const frontNeckDepth = measurements.overrideFNDEnabled
    ? n(measurements.overrideFND, autoFrontNeckDepth)
    : autoFrontNeckDepth;
  const backNeckWidth = measurements.overrideBNWEnabled
    ? n(measurements.overrideBNW, autoBackNeckWidth)
    : autoBackNeckWidth;
  const frontNeckWidth = measurements.overrideFNWEnabled
    ? n(measurements.overrideFNW, autoFrontNeckWidth)
    : autoFrontNeckWidth;

  const CB0 = rp(cbX, originY);
  const BHPS = rp(cbX + backNeckWidth, bhpsY);
  const CB1 = rp(cbX, bhpsY + backNeckDepth);

  const BLPS = rp(cbX + shoulder / 2, bhpsY + n(measurements.backShoulderSlope, 2.25));

  const backToFtHps = n(measurements.backToFtHps, 0.5);
  const backShoulderLen = dist(BHPS, BLPS);
  let CF0 = rp(cfX, CB0.y + backToFtHps);
  let FHPS = rp(CF0.x - frontNeckWidth, CF0.y);
  const frontShoulderSlope = n(measurements.frontShoulderSlope, 2.25);
  const targetFrontShoulderLen = Math.max(Math.abs(frontShoulderSlope) + 0.01, backShoulderLen - 0.25);
  const targetFrontShoulderRun = Math.sqrt(Math.max((targetFrontShoulderLen ** 2) - (frontShoulderSlope ** 2), 0.0001));
  let FLPS = rp(FHPS.x - targetFrontShoulderRun, FHPS.y + frontShoulderSlope);

  // Keep average scye depth anchored to user input regardless of Bk→FT HPS shifts
  const chestY = ((BLPS.y + FLPS.y) / 2) + armholeScyeDepth;
  const waistY = BHPS.y + n(measurements.distanceToWaist, 17.5);
  const bellyY = waistY + n(measurements.distanceToBelly, 5);
  const hemY = CB1.y + jacketLengthBack;

  const CB2 = rp(cbX, BLPS.y);
  const CB4 = rp(cb4XBase, chestY);
  const autoAcrossBackDepth = roundToSixteenth(Math.max(0.5, ((CB1.y + CB4.y) / 2) - CB0.y));
  const resolvedAcrossBackDepth = measurements.overrideAcrossBackDepthEnabled
    ? n(measurements.overrideAcrossBackDepth, autoAcrossBackDepth)
    : autoAcrossBackDepth;
  const CB3 = rp(cbX + 0.125, CB0.y + resolvedAcrossBackDepth);
  const CB5 = rp(cbX + 1.25 + n(measurements.jacketCb5Dx, 0), waistY);
  const CB6 = rp(cbX + 1.125 + n(measurements.jacketCb6Dx, 0), bellyY);
  const CB7 = rp(cbX + 1.125 + n(measurements.jacketCb7Dx, 0), hemY);

  const BXB = rp(CB3.x + (acrossBack / 2), CB3.y);
  const BAHP = rp(BLPS.x + 0.25 + n(measurements.jacketBahpDx, 0), chestY - 2.75 + n(measurements.jacketBahpDy, 0));

  const backChestSplitInput = n(measurements.backChestSplit, NaN);
  const frontChestSplitInput = n(measurements.frontChestSplit, NaN);
  const hasExplicitChestSplit = Number.isFinite(backChestSplitInput) && Number.isFinite(frontChestSplitInput);

  const backChestWidthSeed = hasExplicitChestSplit ? backChestSplitInput : (BAHP.x - (cbX + 0.625));

  const BCPS = rp(BAHP.x - 0.375 + n(measurements.backChestBalanceDx, 0), chestY);
  const BWPS = rp(BCPS.x - 0.5, waistY);
  const BBPS = rp(BWPS.x + 0.25, bellyY);
  const BSPS = rp(BBPS.x + 0.25, hemY);

  // Hard chest lock while keeping SCPF tied to FCPS and SCPB anchored from SAHP.
  // chestHalf = (CF2-FCPS) + (BCPS-CB4) + (SCPF-SCPB), where SCPF = FCPS + scpfDx.
  // This simplifies to: chestHalf = CF2 + BCPS - CB4 - SCPB + scpfDx.
  const scpfDx = n(measurements.jacketScpfDx, 0);
  const chestLockSahpX = BAHP.x + n(measurements.jacketSahpDx, 0);
  const chestLockScpbX = chestLockSahpX + n(measurements.jacketScpbDx, 0);
  const chestLockedCfX = chestHalf - BCPS.x + (CB4.x + n(measurements.jacketCb4Dx, 0)) + chestLockScpbX - scpfDx;
  cfX = chestLockedCfX;

  CF0 = rp(cfX, CB0.y + backToFtHps);
  FHPS = rp(CF0.x - frontNeckWidth, CF0.y);
  FLPS = rp(FHPS.x - targetFrontShoulderRun, FHPS.y + frontShoulderSlope);

  const RL0 = rp(FHPS.x + 1, FHPS.y);

  const autoAcrossFrontDepth = roundToSixteenth(Math.max(0.5, (chestY - 2.5) - CF0.y));
  const resolvedAcrossFrontDepth = measurements.overrideAcrossFrontDepthEnabled
    ? n(measurements.overrideAcrossFrontDepth, autoAcrossFrontDepth)
    : autoAcrossFrontDepth;
  const CF1 = rp(cfX, CF0.y + resolvedAcrossFrontDepth);
  const CF2 = rp(cfX, chestY);
  const frontChestWidthSeed = hasExplicitChestSplit ? frontChestSplitInput : roundToEighth(CF2.x - roundToEighth(FLPS.x));
  const FCPS = rp(roundToEighth(FLPS.x) + n(measurements.frontChestBalanceDx, 0), chestY);
  const CF3 = rp(cfX + bellyProfileRule.cfForwardOffset + n(measurements.jacketCf3Dx, 0), waistY);
  const RL1Base = rp(CF3.x + (isDoubleBreasted ? 2.5 : 0.5), CF3.y);
  const lapelAnchorPointMap = {
    RL1: RL1Base,
  };
  const lapelAnchorOffseted = applyPointHandleOffsets(lapelAnchorPointMap, measurements);
  const RL1 = lapelAnchorOffseted.points.RL1;
  const rollDirection = normalize({ x: RL1.x - RL0.x, y: RL1.y - RL0.y }, { x: 0, y: 1 });
  const FND = rp(
    RL0.x + rollDirection.x * frontNeckDepth,
    RL0.y + rollDirection.y * frontNeckDepth,
  );

  const FWPS = rp(FCPS.x + bellyProfileRule.frontSideOffset + n(measurements.jacketFwpsDx, 0), waistY);
  const CF4 = rp(CF3.x + (isDoubleBreasted ? 0 : -0.5) + n(measurements.jacketCf4Dx, 0), bellyY);
  const FBPS = rp(FCPS.x + bellyProfileRule.frontSideOffset + n(measurements.jacketFbpsDx, 0), bellyY);
  const frontHemY = FHPS.y + jacketLengthFront;
  const frontSeatY = frontHemY - 0.5;
  const CF5 = rp(isDoubleBreasted ? CF3.x : cfX, frontSeatY);
  const FSPS = rp(FCPS.x + bellyProfileRule.frontSideOffset + n(measurements.jacketFspsDx, 0), frontSeatY);
  const FXF = rp(CF1.x - (acrossFront / 2), CF1.y);

  const EB = rp(isDoubleBreasted ? RL1.x : CF4.x + 0.5, CF4.y);

  let SAHP = rp(BAHP.x + n(measurements.jacketSahpDx, 0), BAHP.y + n(measurements.jacketSahpDy, 0));
  let SCPB = rp(BAHP.x + n(measurements.jacketScpbDx, 0), chestY);
  let SCPF = rp(SCPB.x + scpfDx, chestY);
  let SWPF = rp(SCPF.x + bellyProfileRule.swpfOffsetFromScpf + n(measurements.jacketSwpfDx, 0), waistY);
  let SWPB = rp(SWPF.x + n(measurements.jacketSwpbDx, 0), waistY);
  let SBPF = rp(SWPF.x + 0.25 + n(measurements.jacketSbpfDx, 0), bellyY);
  let SBPB = rp(SBPF.x + n(measurements.jacketSbpbDx, 0), bellyY);
  let SSPF = rp(SBPF.x + n(measurements.jacketSspfDx, 0), frontHemY);
  let SSPB = rp(SSPF.x + n(measurements.jacketSspbDx, 0), hemY);

  const jacketDartMode = normalizeJacketDartMode(
    measurements?.jacketDartMode
      ?? ((measurements.fishDartEnabled !== false && measurements.fishDartEnabled !== 0 && measurements.fishDartEnabled !== '0') ? 'belly_and_fish' : 'off'),
  );
  const fishDartEnabled = jacketDartMode !== 'off';
  const bellyFishDartEnabled = jacketDartMode === 'belly_and_fish';
  const fishOnlyDartEnabled = jacketDartMode === 'fish';
  const dartWidthWaist = clamp(n(measurements.dartWidthWaist, 0.5), 0.125, 2);
  const dartWidthBelly = clamp(n(measurements.dartWidthBelly, 0.5), 0.125, 2);
  const dartLengthTop = clamp(n(measurements.dartLengthTop, 5), 1, 12);
  const dartLengthBottom = clamp(n(measurements.dartLengthBottom, 4), 1, 12);
  const dartWaistShift = n(measurements.dartWaistShift, 0);
  const dartAnchorX = CF3.x + (FWPS.x - CF3.x) * 0.6;
  const FD1 = rp(dartAnchorX - dartWaistShift, CF3.y);
  const FD2 = rp(FD1.x + dartWidthWaist, CF3.y);
  const FD0 = rp((FD1.x + FD2.x) / 2, CF3.y - dartLengthTop);
  const dartBellyCenterX = FD0.x;
  const fishDartBottomApex = rp(dartBellyCenterX, CF3.y + dartLengthBottom);
  const dartBellyLeftPoint = rp(dartBellyCenterX - (dartWidthBelly / 2), CF3.y + dartLengthBottom);
  const dartBellyRightPoint = rp(dartBellyCenterX + (dartWidthBelly / 2), CF3.y + dartLengthBottom);
  const FD3 = bellyFishDartEnabled
    ? dartBellyLeftPoint
    : fishDartBottomApex;
  const FD4 = bellyFishDartEnabled
    ? dartBellyRightPoint
    : null;

  const frontSideDirection = normalize({ x: FSPS.x - FCPS.x, y: FSPS.y - FCPS.y }, { x: 1, y: 0 });
  const sideNormalOutward = normalize({ x: -frontSideDirection.y, y: frontSideDirection.x }, { x: 1, y: 0 });
  const seamHorizontalRatio = Math.abs(FSPS.y - FCPS.y) < 1e-8
    ? 0
    : clamp((FD3.y - FCPS.y) / (FSPS.y - FCPS.y), 0, 1);
  const BD1 = rp(
    FBPS.x,
    fishDartBottomApex.y,
  );
  const BD2Base = rp(
    BD1.x - (frontSideDirection.x * 0.25),
    BD1.y - (frontSideDirection.y * 0.25),
  );
  const BD2 = rp(
    BD2Base.x + (sideNormalOutward.x * dartWidthBelly),
    BD2Base.y + (sideNormalOutward.y * dartWidthBelly),
  );
  const SP1 = rp(
    isDoubleBreasted ? EB.x : dartBellyLeftPoint.x + n(measurements.jacketSp1Dx, 0),
    frontSeatY + 0.5 + n(measurements.jacketSp1Dy, 0),
  );

  const chestPocketEnabled = measurements.chestPocketEnabled !== false && measurements.chestPocketEnabled !== 0 && measurements.chestPocketEnabled !== '0';
  const chestPocketWidth = clamp(n(measurements.chestPocketWidth, 4), 1.5, 8);
  const chestPocketDepth = clamp(n(measurements.chestPocketDepth, 0.875), 0.25, 2.5);
  const chestPocketAboveChest = clamp(n(measurements.chestPocketAboveChest, 0.5), 0, 4);
  const chestPocketArmholeLift = clamp(n(measurements.chestPocketArmholeLift, 0.25), -1.5, 1.5);

  const chestPocketDirection = normalize({ x: chestPocketWidth, y: chestPocketArmholeLift }, { x: 1, y: 0 });
  const chestPocketUpNormal = normalize({ x: chestPocketDirection.y, y: -chestPocketDirection.x }, { x: 0, y: -1 });
  const chestPocketCenter = rp(FD0.x, chestY - chestPocketAboveChest);
  const chestPocketHalfWidth = chestPocketWidth / 2;
  const chestPocketHalfDepth = chestPocketDepth / 2;

  let CPKT1 = null;
  let CPKT2 = null;
  let CPKT3 = null;
  let CPKT4 = null;

  if (chestPocketEnabled) {
    const chestPocketTopCenter = rp(
      chestPocketCenter.x + (chestPocketUpNormal.x * chestPocketHalfDepth),
      chestPocketCenter.y + (chestPocketUpNormal.y * chestPocketHalfDepth),
    );
    const chestPocketBottomCenter = rp(
      chestPocketCenter.x - (chestPocketUpNormal.x * chestPocketHalfDepth),
      chestPocketCenter.y - (chestPocketUpNormal.y * chestPocketHalfDepth),
    );

    CPKT1 = rp(
      chestPocketTopCenter.x - (chestPocketDirection.x * chestPocketHalfWidth),
      chestPocketTopCenter.y - (chestPocketDirection.y * chestPocketHalfWidth),
    );
    CPKT2 = rp(
      chestPocketTopCenter.x + (chestPocketDirection.x * chestPocketHalfWidth),
      chestPocketTopCenter.y + (chestPocketDirection.y * chestPocketHalfWidth),
    );
    CPKT4 = rp(
      chestPocketBottomCenter.x - (chestPocketDirection.x * chestPocketHalfWidth),
      chestPocketBottomCenter.y - (chestPocketDirection.y * chestPocketHalfWidth),
    );
    CPKT3 = rp(
      chestPocketBottomCenter.x + (chestPocketDirection.x * chestPocketHalfWidth),
      chestPocketBottomCenter.y + (chestPocketDirection.y * chestPocketHalfWidth),
    );
  }

  const sidePocketEnabled = measurements.sidePocketEnabled !== false && measurements.sidePocketEnabled !== 0 && measurements.sidePocketEnabled !== '0';
  const sidePocketLength = clamp(n(measurements.sidePocketLength, 6), 2, 10);
  const sidePocketStartOffset = clamp(n(measurements.sidePocketStartOffset, 0.5), 0, 4);

  let SPK1 = null;
  let SPK2 = null;

  if (sidePocketEnabled) {
    const sidePocketStartReference = bellyFishDartEnabled && FD4 ? FD4 : fishDartBottomApex;
    const sidePocketEndReference = bellyFishDartEnabled ? BD1 : rp(FBPS.x, fishDartBottomApex.y);
    const sidePocketLineDx = sidePocketEndReference.x - sidePocketStartReference.x;
    const sidePocketLineDy = sidePocketEndReference.y - sidePocketStartReference.y;
    const sidePocketLineUnit = normalize({ x: sidePocketLineDx, y: sidePocketLineDy }, { x: -1, y: 0 });

    if (Math.abs(sidePocketLineDx) > 1e-8) {
      const startX = sidePocketStartReference.x + sidePocketStartOffset;
      const interpolation = (startX - sidePocketStartReference.x) / sidePocketLineDx;
      const startY = sidePocketStartReference.y + (sidePocketLineDy * interpolation);
      SPK1 = rp(startX, startY);
    } else {
      SPK1 = rp(
        sidePocketStartReference.x - (sidePocketLineUnit.x * sidePocketStartOffset),
        sidePocketStartReference.y - (sidePocketLineUnit.y * sidePocketStartOffset),
      );
    }

    SPK2 = rp(
      SPK1.x + (sidePocketLineUnit.x * sidePocketLength),
      SPK1.y + (sidePocketLineUnit.y * sidePocketLength),
    );
  }

  const basePointMap = {
    CB4,
    BLPS,
    BXB,
    FLPS,
    FXF,
    BWPS,
    FWPS,
    BBPS,
    FBPS,
    BSPS,
    FSPS,
    SP1,
  };

  const pointOffseted = applyPointHandleOffsets(basePointMap, measurements);

  const resolved = {
    ...basePointMap,
    ...pointOffseted.points,
  };

  const rawLapelType = String(measurements?.lapelType || 'pointed_notch').toLowerCase();
  const lapelType = ['pointed_notch', 'rounded_notch', 'peak', 'shawl', 'diamond_shawl'].includes(rawLapelType)
    ? rawLapelType
    : 'pointed_notch';
  const lapelWidth = clamp(n(measurements.lapelWidth, 3.5), 1.25, 5.5);
  const gorgePointDepth = clamp(n(measurements.gorgePointDepth, 5), 1.5, 8);

  // Updated notch logic:
  // LP1 starts on roll line at user-defined gorge depth from RL0 towards RL1.
  // LP0 shares LP1.y and is lapelWidth to the left.
  const LP1Seed = rp(
    RL0.x + (rollDirection.x * gorgePointDepth),
    RL0.y + (rollDirection.y * gorgePointDepth),
  );
  const LP0Seed = rp(
    LP1Seed.x - lapelWidth,
    LP1Seed.y,
  );
  const lapelPointMap = {
    LP1: LP1Seed,
    LP0: LP0Seed,
  };
  const lapelPointOffseted = applyPointHandleOffsets(lapelPointMap, measurements);
  const lapelPointHandlesForMode = lapelType === 'peak'
    ? lapelPointOffseted.pointHandles.filter((handle) => handle.name !== 'LP1')
    : lapelPointOffseted.pointHandles;
  let LP1 = lapelType === 'peak' ? LP1Seed : lapelPointOffseted.points.LP1;
  let LP0 = lapelPointOffseted.points.LP0;
  let LP2 = midpoint(LP0, LP1);
  let LP3 = rp(LP0.x, LP2.y - 1);

  // GP / CP0 differ by lapel model.
  const lp0ToFnd = normalize({ x: FND.x - LP0.x, y: FND.y - LP0.y }, { x: 1, y: 0 });
  let GP = lapelType === 'peak'
    ? rp(LP2.x, LP2.y)
    : rp(
      LP0.x + (lp0ToFnd.x * 1),
      LP0.y + (lp0ToFnd.y * 1),
    );

  const lp0ToFndNormal = normalize({ x: -lp0ToFnd.y, y: lp0ToFnd.x }, { x: 0, y: 1 });
  const cp0CandidateA = rp(
    GP.x + (lp0ToFndNormal.x * 1),
    GP.y + (lp0ToFndNormal.y * 1),
  );
  const cp0CandidateB = rp(
    GP.x - (lp0ToFndNormal.x * 1),
    GP.y - (lp0ToFndNormal.y * 1),
  );
  const cp0CandidateADisplayY = -cp0CandidateA.y;
  const cp0CandidateBDisplayY = -cp0CandidateB.y;
  const peakDirection = normalize({ x: LP2.x - LP3.x, y: LP2.y - LP3.y }, { x: -1, y: 0 });
  const peakCp0Base = rp(
    LP3.x + (peakDirection.x * 0.5),
    LP3.y + (peakDirection.y * 0.5),
  );
  const cp0Base = lapelType === 'peak'
    ? peakCp0Base
    : (cp0CandidateADisplayY <= cp0CandidateBDisplayY ? cp0CandidateA : cp0CandidateB);
  let CP0 = rp(
    cp0Base.x + n(measurements.jacketCpoDx, 0),
    cp0Base.y + n(measurements.jacketCpoDy, 0),
  );

  // Buttonhole marker: LB0 at x=LP0.x-1 and LB0→LB1 length 0.75 parallel to LP0→FND.
  const buttonholeAnchorPoint = lapelType === 'peak' ? rp(LP2.x, LP2.y + 1.25) : LP0;
  const buttonholeDirection = normalize(
    lapelType === 'peak'
      ? { x: LP3.x - LP2.x, y: LP3.y - LP2.y }
      : { x: FND.x - LP0.x, y: FND.y - LP0.y },
    { x: 1, y: 0 },
  );
  const LB0 = rp(
    buttonholeAnchorPoint.x + (lapelType === 'peak' ? 0 : 1) + n(measurements.jacketLboDx, 0),
    buttonholeAnchorPoint.y + (lapelType === 'peak' ? 0 : 1) + n(measurements.jacketLboDy, 0),
  );
  const LB1 = rp(
    LB0.x + (buttonholeDirection.x * 0.75),
    LB0.y + (buttonholeDirection.y * 0.75),
  );

  let LP0_M = rp(LP0.x, LP0.y);
  let LP2_M = rp(LP2.x, LP2.y);
  let LP3_M = rp(LP3.x, LP3.y);
  let GP_M = rp(GP.x, GP.y);
  let CP0_M = rp(CP0.x, CP0.y);
  let RL1_M = rp(RL1.x, RL1.y);
  let LB0_M = rp(LB0.x, LB0.y);
  let LB1_M = rp(LB1.x, LB1.y);
  let lapelCutOutlinePath = '';
  let lapelConstructionLines = [];

  const lapelFormulaLines = [
    'Active Lapel Draft:',
    'LP1 = RL0 + normalize(RL1-RL0) * gorgePointDepth (default 5")',
    'LP0.y = LP1.y; LP0.x = LP1.x - lapelWidth (default 3.5")',
    lapelType === 'rounded_notch'
      ? 'Rounded notch base: LP0 stays visible as a reference; RN1 sits 0.75" toward FND and RN2 sits 0.75" toward RL1, so the live contour is FND→RN1→RN2→RL1.'
      : 'Primary lapel lines: FND→LP0 and LP0→RL1; pointed-notch LP0→RL1 is an editable reflected curve.',
    isDoubleBreasted
      ? 'Double-breasted front closure: RL1.x = CF3.x + 2.5", EB.x = SP1.x = RL1.x, and CF4.x = CF5.x = CF3.x. The RL1→EB→SP1 edge stays straight.'
      : 'Single-breasted front closure: RL1 is seeded 0.5" ahead of CF3 and the front edge blends through EB→SP1 with the front hem curve.',
    'GP lies on LP0→FND at 1" from LP0 toward FND',
    lapelType === 'rounded_notch'
      ? 'CP0 is still derived 1" from GP, perpendicular to LP0→FND (+jacketCpoDx, +jacketCpoDy), but CP0 remains a visible collar reference only while the live collar corner rounds around it.'
      : 'CP0 is 1" from GP, perpendicular to LP0→FND (+jacketCpoDx, +jacketCpoDy)',
    ...(isShawlFamilyLapelType(lapelType) ? [
      lapelType === 'diamond_shawl'
        ? 'Diamond shawl omits LB0/LB1 and uses RL2 on the outer contour between TCOL2 and RL1.'
        : 'Shawl lapel buttonholes LB0/LB1 and their reflected points are omitted.',
    ] : [
      'LB0 is right by 1 and down by 1 from LP0 (+jacketLboDx, +jacketLboDy)',
      'LB0→LB1 length = 0.75" parallel to LP0→FND',
      lapelType === 'rounded_notch'
        ? 'Reflect FND→RN1, the RN1→RN2 rounded corner, RN2→RL1, and LB0→LB1 across the roll line (RL0→RL1). LP0 remains visible but is not forced onto the cut contour.'
        : 'Reflect lines FND→LP0, GP→CP0, LP0→RL1, LB0→LB1 across roll line (RL0→RL1) as cut outline.',
    ]),
  ];

  const CB3Resolved = rp(cbX + 0.125, CB0.y + resolvedAcrossBackDepth);
  const BXBResolved = rp(CB3Resolved.x + (acrossBack / 2) + n(measurements.jacketBxbDx, 0), CB3Resolved.y);
  resolved.BXB = BXBResolved;

  const frontChestWidth = CF2.x - FCPS.x;
  const backChestWidth = BCPS.x - resolved.CB4.x;
  const frontWaistWidth = CF3.x - FWPS.x;
  const backWaistWidth = BWPS.x - CB5.x;
  const frontBellyWidth = CF4.x - FBPS.x;
  const backBellyWidth = BBPS.x - CB6.x;
  const frontSeatWidth = CF5.x - FSPS.x;
  const backSeatWidth = BSPS.x - CB7.x;

  const fishDartEnabledForBalance = jacketDartMode !== 'off';
  const dartWaistIntakeForBalance = fishDartEnabledForBalance ? Math.abs(n(measurements.dartWidthWaist, 0.5)) : 0;
  const frontWaistNetForBalance = frontWaistWidth - dartWaistIntakeForBalance;

  const sideChest = clamp(chestHalf - frontChestWidth - backChestWidth, 0.75, 10);
  const sideWaist = clamp(waistHalf - frontWaistNetForBalance - backWaistWidth, 0.75, 10);
  const sideBelly = clamp(bellyHalf - frontBellyWidth - backBellyWidth, 0.75, 10);
  const sideSeat = clamp(seatHalf - frontSeatWidth - backSeatWidth, 0.75, 10);

  SAHP = rp(BAHP.x + n(measurements.jacketSahpDx, 0), BAHP.y + n(measurements.jacketSahpDy, 0));
  SCPB = rp(SAHP.x + n(measurements.jacketScpbDx, 0), chestY);
  SCPF = rp(FCPS.x + scpfDx, chestY);

  SWPF = rp(SCPF.x + bellyProfileRule.swpfOffsetFromScpf + n(measurements.jacketSwpfDx, 0), waistY);
  SWPB = rp(SWPF.x - sideWaist + n(measurements.jacketSwpbDx, 0), waistY);

  SBPF = rp(SWPF.x + 0.25 + n(measurements.jacketSbpfDx, 0), bellyY);
  SBPB = rp(SBPF.x - sideBelly + n(measurements.jacketSbpbDx, 0), bellyY);

  SSPF = rp(SBPF.x + n(measurements.jacketSspfDx, 0), frontHemY);
  SSPB = rp(SSPF.x - sideSeat + n(measurements.jacketSspbDx, 0), frontHemY);

  const pointHandlesResolved = [...pointOffseted.pointHandles, ...lapelAnchorOffseted.pointHandles, ...lapelPointHandlesForMode].map((handle) => {
    if (handle.name === 'CB4') {
      return {
        ...handle,
        point: resolved.CB4,
      };
    }

    if (handle.name === 'BXB') {
      return {
        ...handle,
        point: BXBResolved,
        dy: 0,
      };
    }

    return handle;
  });

  [
    ['CB0', CB0],
    ['CB1', CB1],
    ['CB2', CB2],
    ['CB3', CB3Resolved],
    ['CB4', resolved.CB4],
    ['CB5', CB5],
    ['CB6', CB6],
    ['CB7', CB7],
    ['BHPS', BHPS],
    ['BLPS', resolved.BLPS],
    ['BXB', BXBResolved],
    ['BAHP', BAHP],
    ['BCPS', BCPS],
    ['BWPS', resolved.BWPS],
    ['BBPS', resolved.BBPS],
    ['BSPS', resolved.BSPS],
    ['CF0', CF0],
    ['CF1', CF1],
    ['CF2', CF2],
    ['CF3', CF3],
    ['CF4', CF4],
    ['CF5', CF5],
    ['FHPS', FHPS],
    ['FLPS', resolved.FLPS],
    ['FND', FND],
    ['FXF', resolved.FXF],
    ['FCPS', FCPS],
    ['FWPS', resolved.FWPS],
    ['FBPS', resolved.FBPS],
    ['FSPS', resolved.FSPS],
    ['BD1', BD1],
    ['BD2', BD2],
    ['EB', EB],
    ['RL0', RL0],
    ['RL1', RL1],
    ['LP1', LP1],
    ['LP0', LP0],
    ...(!isShawlFamilyLapelType(lapelType) ? [
      ['GP', GP],
      ['CP0', CP0],
    ] : []),
    ...(!isShawlFamilyLapelType(lapelType) ? [
      ['LB0', LB0],
      ['LB1', LB1],
    ] : []),
    ['SP1', SP1],
    ['CPKT1', CPKT1],
    ['CPKT2', CPKT2],
    ['CPKT3', CPKT3],
    ['CPKT4', CPKT4],
    ['SPK1', SPK1],
    ['SPK2', SPK2],
    ['SAHP', SAHP],
    ['SCPB', SCPB],
    ['SCPF', SCPF],
    ['SWPB', SWPB],
    ['SWPF', SWPF],
    ['SBPB', SBPB],
    ['SBPF', SBPF],
    ['SSPB', SSPB],
    ['SSPF', SSPF],
  ].forEach(([name, point]) => applyResolvedPointDeltaToPoint(name, point));

  // Final enforcement: rebuild lapel-specific guide points from the resolved roll line.
  if (lapelType === 'peak') {
    LP1 = rp(
      RL0.x + (rollDirection.x * gorgePointDepth),
      RL0.y + (rollDirection.y * gorgePointDepth),
    );
    LP2 = midpoint(LP0, LP1);
    const targetPeakY = LP2.y - 1;
    const rl1ToLp0 = { x: LP0.x - RL1.x, y: LP0.y - RL1.y };
    const peakT = Math.abs(rl1ToLp0.y) > 1e-8
      ? (targetPeakY - RL1.y) / rl1ToLp0.y
      : 1.25;
    LP3 = rp(
      RL1.x + (rl1ToLp0.x * peakT),
      targetPeakY,
    );
    GP = rp(LP2.x, LP2.y);
    const peakCp0Direction = normalize({ x: LP2.x - LP3.x, y: LP2.y - LP3.y }, { x: -1, y: 0 });
    const peakCp0BaseResolved = rp(
      LP3.x + (peakCp0Direction.x * 0.5),
      LP3.y + (peakCp0Direction.y * 0.5),
    );
    CP0 = rp(
      peakCp0BaseResolved.x + n(measurements.jacketCpoDx, 0),
      peakCp0BaseResolved.y + n(measurements.jacketCpoDy, 0),
    );
    applyResolvedPointDeltaToPoint('LP2', LP2);
    applyResolvedPointDeltaToPoint('LP3', LP3);
    GP = rp(LP2.x, LP2.y);
    const refreshedPeakCp0Direction = normalize({ x: LP2.x - LP3.x, y: LP2.y - LP3.y }, { x: -1, y: 0 });
    const refreshedPeakCp0Base = rp(
      LP3.x + (refreshedPeakCp0Direction.x * 0.5),
      LP3.y + (refreshedPeakCp0Direction.y * 0.5),
    );
    CP0 = rp(
      refreshedPeakCp0Base.x + n(measurements.jacketCpoDx, 0),
      refreshedPeakCp0Base.y + n(measurements.jacketCpoDy, 0),
    );
    const refreshedButtonholeAnchor = rp(LP2.x, LP2.y + 1.25);
    const refreshedButtonholeDirection = normalize({ x: LP3.x - LP2.x, y: LP3.y - LP2.y }, { x: 1, y: 0 });
    LB0.x = refreshedButtonholeAnchor.x + n(measurements.jacketLboDx, 0);
    LB0.y = refreshedButtonholeAnchor.y + n(measurements.jacketLboDy, 0);
    LB1.x = LB0.x + (refreshedButtonholeDirection.x * 0.75);
    LB1.y = LB0.y + (refreshedButtonholeDirection.y * 0.75);

    applyResolvedPointDeltaToPoint('CP0', CP0);
    applyResolvedPointDeltaToPoint('LB0', LB0);
    applyResolvedPointDeltaToPoint('LB1', LB1);
  } else if (lapelType === 'pointed_notch' || lapelType === 'rounded_notch') {
    const vec = normalize({ x: FND.x - LP0.x, y: FND.y - LP0.y }, { x: 1, y: 0 });
    const normal = normalize({ x: -vec.y, y: vec.x }, { x: 0, y: 1 });
    const cp0A = rp(GP.x + normal.x * 1 + n(measurements.jacketCpoDx, 0), GP.y + normal.y * 1 + n(measurements.jacketCpoDy, 0));
    const cp0B = rp(GP.x - normal.x * 1 + n(measurements.jacketCpoDx, 0), GP.y - normal.y * 1 + n(measurements.jacketCpoDy, 0));
    CP0 = cp0A.y <= cp0B.y ? cp0A : cp0B;
    applyResolvedPointDeltaToPoint('CP0', CP0);
  }

  // Enforce LP1 on roll line RL0→RL1 even after any direct point deltas.
  const rlDenom = (RL1.x - RL0.x) ** 2 + (RL1.y - RL0.y) ** 2;
  if (rlDenom > 1e-8) {
    const tFnd = (((FND.x - RL0.x) * (RL1.x - RL0.x)) + ((FND.y - RL0.y) * (RL1.y - RL0.y))) / rlDenom;
    FND.x = RL0.x + (RL1.x - RL0.x) * tFnd;
    FND.y = RL0.y + (RL1.y - RL0.y) * tFnd;

    if (lapelType !== 'peak') {
      const tLp1 = (((LP1.x - RL0.x) * (RL1.x - RL0.x)) + ((LP1.y - RL0.y) * (RL1.y - RL0.y))) / rlDenom;
      LP1.x = RL0.x + (RL1.x - RL0.x) * tLp1;
      LP1.y = RL0.y + (RL1.y - RL0.y) * tLp1;
    }
  }

  const lp0ToFndResolved = normalize({ x: FND.x - LP0.x, y: FND.y - LP0.y }, { x: 1, y: 0 });
  if (lapelType === 'pointed_notch' || lapelType === 'rounded_notch') {
    GP = rp(
      LP0.x + (lp0ToFndResolved.x * 1),
      LP0.y + (lp0ToFndResolved.y * 1),
    );
  }

  // Rebuild mirrored lapel geometry from final resolved points (after any point deltas)
  // so LP0→RL1 and mirrored cut lines stay perfectly connected.
  LP0_M = reflectPointAcrossLine(LP0, RL0, RL1);
  LP2_M = reflectPointAcrossLine(LP2, RL0, RL1);
  LP3_M = reflectPointAcrossLine(LP3, RL0, RL1);
  GP_M = reflectPointAcrossLine(GP, RL0, RL1);
  CP0_M = reflectPointAcrossLine(CP0, RL0, RL1);
  RL1_M = reflectPointAcrossLine(RL1, RL0, RL1);
  LB0_M = reflectPointAcrossLine(LB0, RL0, RL1);
  LB1_M = reflectPointAcrossLine(LB1, RL0, RL1);
  if (isShawlFamilyLapelType(lapelType)) {
    applyResolvedPointDeltaToPoint('LP0_M', LP0_M);
  }
  const pointedLapelFallCtrl = midpoint(
    LP0,
    RL1,
    n(measurements.jacketLapelFallCurveCtrlDx, 0),
    n(measurements.jacketLapelFallCurveCtrlDy, 0),
  );
  const pointedLapelFallCtrlMirror = reflectPointAcrossLine(pointedLapelFallCtrl, RL0, RL1);
  const pointedNotchRollFallLength = quadLength(LP0, pointedLapelFallCtrl, RL1);
  const roundedNotchOffset = 0.75;
  const rn1Direction = normalize({ x: FND.x - LP0.x, y: FND.y - LP0.y }, { x: 1, y: 0 });
  const rn2Direction = normalize({ x: RL1.x - LP0.x, y: RL1.y - LP0.y }, { x: 0, y: 1 });
  const RN1 = rp(
    LP0.x + (rn1Direction.x * roundedNotchOffset),
    LP0.y + (rn1Direction.y * roundedNotchOffset),
  );
  const RN2 = rp(
    LP0.x + (rn2Direction.x * roundedNotchOffset),
    LP0.y + (rn2Direction.y * roundedNotchOffset),
  );
  const RN1_M = reflectPointAcrossLine(RN1, RL0, RL1);
  const RN2_M = reflectPointAcrossLine(RN2, RL0, RL1);
  const roundedNotchCornerCtrl = rp(
    LP0.x + n(measurements.jacketRoundedNotchCurveCtrlDx, 0),
    LP0.y + n(measurements.jacketRoundedNotchCurveCtrlDy, 0),
  );
  const roundedNotchCornerCtrlMirror = reflectPointAcrossLine(roundedNotchCornerCtrl, RL0, RL1);
  const roundedNotchLapelFallCtrl = midpoint(
    RN2,
    RL1,
    n(measurements.jacketLapelFallCurveCtrlDx, 0),
    n(measurements.jacketLapelFallCurveCtrlDy, 0),
  );
  const roundedNotchLapelFallCtrlMirror = reflectPointAcrossLine(roundedNotchLapelFallCtrl, RL0, RL1);
  const peakLapelFallCtrl = midpoint(
    LP3,
    RL1,
    n(measurements.jacketPeakLapelFallCurveCtrlDx, 0),
    n(measurements.jacketPeakLapelFallCurveCtrlDy, 0),
  );
  const peakLapelFallCtrlMirror = reflectPointAcrossLine(peakLapelFallCtrl, RL0, RL1);

  const pointedNotchPath = `
    M ${toSVG(FND)} L ${toSVG(LP0)}
    M ${toSVG(LP0)} Q ${toSVG(pointedLapelFallCtrl)} ${toSVG(RL1)}
    M ${toSVG(GP)} L ${toSVG(CP0)}
    M ${toSVG(LB0)} L ${toSVG(LB1)}
    M ${toSVG(FND)} L ${toSVG(LP0_M)}
    M ${toSVG(LP0_M)} Q ${toSVG(pointedLapelFallCtrlMirror)} ${toSVG(RL1_M)}
    M ${toSVG(GP_M)} L ${toSVG(CP0_M)}
    M ${toSVG(LB0_M)} L ${toSVG(LB1_M)}
  `.replace(/\s+/g, ' ').trim();

  const pointedNotchLines = [
    { id: 'lapel-fnd-lp0', p1: FND, p2: LP0, label: 'FND→LP0' },
    { id: 'lapel-gp-cp0', p1: GP, p2: CP0, label: 'GP→CP0' },
    { id: 'lapel-lb0-lb1', p1: LB0, p2: LB1, label: 'LB0→LB1' },
    { id: 'lapel-fnd-lp0-mirror', p1: FND, p2: LP0_M, label: 'Mirror FND→LP0' },
    { id: 'lapel-gp-cp0-mirror', p1: GP_M, p2: CP0_M, label: 'Mirror GP→CP0' },
    { id: 'lapel-lb0-lb1-mirror', p1: LB0_M, p2: LB1_M, label: 'Mirror LB0→LB1' },
  ];

  const emptyShawlLapelMetrics = {
    upperStraightLength: 0,
    crownCurveLength: 0,
    fallCurveLength: 0,
    outerCurveLength: 0,
  };
  let shawlLapelDraft = {
    path: '',
    lines: [],
    metrics: { ...emptyShawlLapelMetrics },
    formulaLines: [],
  };
  let diamondShawlLapelDraft = {
    path: '',
    lines: [],
    metrics: {
      upperStraightLength: 0,
      diamondQuadraticLength: 0,
      diamondSegmentLength: 0,
      outerCurveLength: 0,
      fallCurveLength: 0,
    },
    formulaLines: [],
  };
  let RL2 = rp(RL1.x, RL1.y);
  let shawlCurveCtrl1 = RL2;
  let shawlCurveCtrl2 = RL2;
  let diamondShawlCurveCtrl = RL2;
  let shawlReflectedTcol2 = RL1;
  let shawlReflectedCtrl1 = RL2;
  let shawlReflectedCtrl2 = RL2;
  let shawlReflectedRl1 = RL1;
  let shawlFoldedPreviewPath = '';
  let diamondShawlFoldedPreviewPath = '';
  let diamondShawlReflectedTcol2 = RL1;
  let diamondShawlReflectedRl2 = RL2;
  let diamondShawlReflectedCtrl = RL2;
  let diamondShawlReflectedRl1 = RL1;
  const peakLapelDraft = createPeakLapelPath({
    FND,
    LP2,
    LP3,
    RL1,
    GP,
    CP0,
    LB0,
    LB1,
    LP2_M,
    LP3_M,
    RL1_M,
    GP_M,
    CP0_M,
    LB0_M,
    LB1_M,
    fallCtrl: peakLapelFallCtrl,
    fallCtrlMirror: peakLapelFallCtrlMirror,
  });
  let roundedNotchDraft = {
    path: '',
    lines: [],
    metrics: {
      roundedCurveLength: 0,
      fallCurveLength: 0,
      collarRoundLength: 0,
      roundDepth: 0,
    },
    formulaLines: [],
  };

  if (lapelType === 'peak') {
    lapelCutOutlinePath = peakLapelDraft.path;
    lapelConstructionLines = peakLapelDraft.lines;
    lapelFormulaLines.push(...peakLapelDraft.formulaLines);
  } else if (lapelType === 'rounded_notch') {
    lapelFormulaLines.push(
      'Rounded-notch fall control uses RN2→RL1 instead of LP0→RL1.',
      'LP0 remains visible as a reference point and rounded-notch guide, not a forced on-curve anchor.',
    );
  } else if (lapelType === 'pointed_notch') {
    lapelCutOutlinePath = pointedNotchPath;
    lapelConstructionLines = pointedNotchLines;
  }

  pointHandlesResolved.forEach((handle) => {
    if (!handle?.name || !handle?.point) return;
    const currentPoint = resolved[handle.name] || (handle.name === 'BXB' ? BXBResolved : null);
    if (currentPoint) {
      handle.point = currentPoint;
    }
  });

  const backNeckCtrlBase = rp(
    CB1.x + (BHPS.x - CB1.x) * 0.64,
    CB1.y,
  );
  const backNeckCtrl = rp(
    backNeckCtrlBase.x + n(measurements.backNeckCtrlDx, 0),
    backNeckCtrlBase.y + n(measurements.backNeckCtrlDy, 0),
  );
  const frontNeckCtrl = midpoint(FHPS, FND, n(measurements.frontNeckCtrlDx, 0), n(measurements.frontNeckCtrlDy, 0));
  let frontArmPart1Ctrl = midpoint(resolved.FLPS, resolved.FXF, n(measurements.frontArmPart1CtrlDx, 0), n(measurements.frontArmPart1CtrlDy, 0));
  let frontArmPart2Ctrl = midpoint(resolved.FXF, FCPS, n(measurements.frontArmPart2CtrlDx, 0), n(measurements.frontArmPart2CtrlDy, 0));

  let backArmPart1Ctrl = midpoint(resolved.BLPS, resolved.BXB, n(measurements.backArmPart1CtrlDx, 0), n(measurements.backArmPart1CtrlDy, 0));
  let backArmPart2Ctrl = midpoint(resolved.BXB, BAHP, n(measurements.backArmPart2CtrlDx, 0), n(measurements.backArmPart2CtrlDy, 0));

  let frontChestWaistCtrl = midpoint(FCPS, resolved.FWPS, n(measurements.frontChestWaistCtrlDx, 0), n(measurements.frontChestWaistCtrlDy, 0));
  let frontWaistBellyCtrl = midpoint(resolved.FWPS, resolved.FBPS, n(measurements.frontWaistBellyCtrlDx, 0), n(measurements.frontWaistBellyCtrlDy, 0));
  let frontBellySeatCtrl = midpoint(resolved.FBPS, resolved.FSPS, n(measurements.frontBellySeatCtrlDx, 0), n(measurements.frontBellySeatCtrlDy, 0));

  const isNearZero = (value) => Math.abs(n(value, 0)) < 1e-8;
  const hasAnyNonZero = (keys = []) => keys.some((key) => !isNearZero(measurements[key]));

  const ebSp1CtrlBase = midpoint(EB, SP1);
  const frontJacketHemCurveCtrlDx = isNearZero(measurements.frontJacketHemCurveCtrlDx) ? 1.5 : n(measurements.frontJacketHemCurveCtrlDx, 0);
  const frontJacketHemCurveCtrlDy = isNearZero(measurements.frontJacketHemCurveCtrlDy) ? 4.5 : n(measurements.frontJacketHemCurveCtrlDy, 0);
  const ebSp1Ctrl = rp(
    ebSp1CtrlBase.x + frontJacketHemCurveCtrlDx,
    ebSp1CtrlBase.y + frontJacketHemCurveCtrlDy,
  );

  let backChestWaistCtrl = midpoint(BCPS, resolved.BWPS, n(measurements.backChestWaistCtrlDx, 0), n(measurements.backChestWaistCtrlDy, 0));
  let backWaistBellyCtrl = midpoint(resolved.BWPS, resolved.BBPS, n(measurements.backWaistBellyCtrlDx, 0), n(measurements.backWaistBellyCtrlDy, 0));
  let backBellySeatCtrl = midpoint(resolved.BBPS, resolved.BSPS, n(measurements.backBellySeatCtrlDx, 0), n(measurements.backBellySeatCtrlDy, 0));

  const sideFrontArmCtrlDx = isNearZero(measurements.sideFrontArmCtrlDx) ? -0.75 : n(measurements.sideFrontArmCtrlDx, 0);
  const sideFrontArmCtrlDy = isNearZero(measurements.sideFrontArmCtrlDy) ? 1.375 : n(measurements.sideFrontArmCtrlDy, 0);
  let sideFrontArmCtrl = midpoint(SAHP, SCPF, sideFrontArmCtrlDx, sideFrontArmCtrlDy);
  let sideFrontChestWaistCtrl = midpoint(SCPF, SWPF, n(measurements.sideFrontChestWaistCtrlDx, 0), n(measurements.sideFrontChestWaistCtrlDy, 0));
  let sideFrontWaistBellyCtrl = midpoint(SWPF, SBPF, n(measurements.sideFrontWaistBellyCtrlDx, 0), n(measurements.sideFrontWaistBellyCtrlDy, 0));
  let sideFrontBellySeatCtrl = midpoint(SBPF, SSPF, n(measurements.sideFrontBellySeatCtrlDx, 0), n(measurements.sideFrontBellySeatCtrlDy, 0));

  let sideBackArmCtrl = midpoint(SAHP, SCPB, n(measurements.sideBackArmCtrlDx, 0), n(measurements.sideBackArmCtrlDy, 0));
  let sideBackChestWaistCtrl = midpoint(SCPB, SWPB, n(measurements.sideBackChestWaistCtrlDx, 0), n(measurements.sideBackChestWaistCtrlDy, 0));
  let sideBackWaistBellyCtrl = midpoint(SWPB, SBPB, n(measurements.sideBackWaistBellyCtrlDx, 0), n(measurements.sideBackWaistBellyCtrlDy, 0));
  let sideBackBellySeatCtrl = midpoint(SBPB, SSPB, n(measurements.sideBackBellySeatCtrlDx, 0), n(measurements.sideBackBellySeatCtrlDy, 0));

  const backNeckCurveLength = quadLength(CB1, backNeckCtrl, BHPS);
  const frontNeckCollarTrace = findQuadraticSegmentFromEndByLength(FHPS, frontNeckCtrl, FND, 1.375);
  const frontNeckCutPoint = rp(frontNeckCollarTrace.point.x, frontNeckCollarTrace.point.y);
  const frontNeckCollarSegment = frontNeckCollarTrace.segment;
  const shoulderDirection = normalize({ x: resolved.FLPS.x - FHPS.x, y: resolved.FLPS.y - FHPS.y }, { x: -1, y: 0 });
  let UCOL0 = rp(FHPS.x + (shoulderDirection.x * 0.5), FHPS.y + (shoulderDirection.y * 0.5));
  applyResolvedPointDeltaToPoint('UCOL0', UCOL0);
  const collarSpineDirection = normalize({ x: UCOL0.x - frontNeckCutPoint.x, y: UCOL0.y - frontNeckCutPoint.y }, shoulderDirection);
  const legacyUCOL1 = rp(UCOL0.x + (collarSpineDirection.x * backNeckCurveLength), UCOL0.y + (collarSpineDirection.y * backNeckCurveLength));
  const shawlShoulderPerpendicular = choosePerpendicularTowardPoint(shoulderDirection, FHPS, legacyUCOL1);
  let UCOL1 = isShawlFamilyLapelType(lapelType)
    ? rp(FHPS.x + (shawlShoulderPerpendicular.x * backNeckCurveLength), FHPS.y + (shawlShoulderPerpendicular.y * backNeckCurveLength))
    : legacyUCOL1;
  const underCollarPerpendicular = choosePerpendicularTowardPoint(collarSpineDirection, UCOL1, CP0_M);
  let UCOL2 = rp(UCOL1.x + (underCollarPerpendicular.x * 1), UCOL1.y + (underCollarPerpendicular.y * 1));
  let TCOL1 = rp(UCOL2.x + (underCollarPerpendicular.x * 1.5), UCOL2.y + (underCollarPerpendicular.y * 1.5));
  const topCollarPerpendicular = choosePerpendicularTowardPoint(underCollarPerpendicular, TCOL1, CP0_M);
  let TCOL2 = rp(TCOL1.x + (topCollarPerpendicular.x * 1), TCOL1.y + (topCollarPerpendicular.y * 1));

  applyResolvedPointDeltaToPoint('UCOL1', UCOL1);
  applyResolvedPointDeltaToPoint('UCOL2', UCOL2);
  applyResolvedPointDeltaToPoint('TCOL1', TCOL1);
  applyResolvedPointDeltaToPoint('TCOL2', TCOL2);

  const collarMatchStartPoint = isShawlFamilyLapelType(lapelType) ? FHPS : UCOL0;
  const collarMatchSegmentLength = dist(collarMatchStartPoint, UCOL1);
  const collarMatchSegmentLabel = isShawlFamilyLapelType(lapelType) ? 'FHPS-UCOL1' : 'UCOL0-UCOL1';

  const underCollarCurveSpan = dist(UCOL2, FND);
  const underCollarStartBaseLength = Math.max(0.5, Math.min(underCollarCurveSpan * 0.32, 2.25));
  const underCollarEndBaseLength = Math.max(0.5, Math.min(underCollarCurveSpan * 0.28, 2.25));
  const underCollarEndTangent = normalize(
    { x: frontNeckCollarSegment.p1.x - FND.x, y: frontNeckCollarSegment.p1.y - FND.y },
    normalize({ x: frontNeckCutPoint.x - FND.x, y: frontNeckCutPoint.y - FND.y }, { x: -1, y: 0 }),
  );
  const underCollarCurveStartBase = rp(UCOL2.x + (underCollarPerpendicular.x * underCollarStartBaseLength), UCOL2.y + (underCollarPerpendicular.y * underCollarStartBaseLength));
  const underCollarCurveEndBase = rp(FND.x - (underCollarEndTangent.x * underCollarEndBaseLength), FND.y - (underCollarEndTangent.y * underCollarEndBaseLength));
  const underCollarCurveStartCtrl = rp(underCollarCurveStartBase.x + n(measurements.underCollarCurveStartCtrlDx, 0), underCollarCurveStartBase.y + n(measurements.underCollarCurveStartCtrlDy, 0));
  const underCollarCurveEndCtrl = rp(underCollarCurveEndBase.x + n(measurements.underCollarCurveEndCtrlDx, 0), underCollarCurveEndBase.y + n(measurements.underCollarCurveEndCtrlDy, 0));

  const collarRoundOffset = 0.5;
  const collarEntryTangent = normalize(
    { x: TCOL2.x - CP0_M.x, y: TCOL2.y - CP0_M.y },
    normalize({ x: UCOL2.x - CP0_M.x, y: UCOL2.y - CP0_M.y }, { x: -1, y: 0 }),
  );
  const collarExitTangent = normalize(
    { x: GP_M.x - CP0_M.x, y: GP_M.y - CP0_M.y },
    { x: 1, y: 0 },
  );
  const CP2_M = rp(
    CP0_M.x + (collarEntryTangent.x * collarRoundOffset),
    CP0_M.y + (collarEntryTangent.y * collarRoundOffset),
  );
  const CP1_M = rp(
    CP0_M.x + (collarExitTangent.x * collarRoundOffset),
    CP0_M.y + (collarExitTangent.y * collarRoundOffset),
  );
  const topCollarCurveEndPoint = lapelType === 'rounded_notch' ? CP2_M : CP0_M;
  const topCollarCurveSpan = dist(TCOL2, topCollarCurveEndPoint);
  const topCollarStartBaseLength = Math.max(0.5, Math.min(topCollarCurveSpan * 0.3, 2.75));
  const topCollarEndBaseLength = Math.max(0.5, Math.min(topCollarCurveSpan * 0.26, 2.75));
  const topCollarCurveStartBase = rp(TCOL2.x + (topCollarPerpendicular.x * topCollarStartBaseLength), TCOL2.y + (topCollarPerpendicular.y * topCollarStartBaseLength));
  const topCollarCurveEndBase = rp(topCollarCurveEndPoint.x - (collarEntryTangent.x * topCollarEndBaseLength), topCollarCurveEndPoint.y - (collarEntryTangent.y * topCollarEndBaseLength));
  const topCollarCurveStartCtrl = rp(topCollarCurveStartBase.x + n(measurements.topCollarCurveStartCtrlDx, 0), topCollarCurveStartBase.y + n(measurements.topCollarCurveStartCtrlDy, 0));
  const topCollarCurveEndCtrl = rp(topCollarCurveEndBase.x + n(measurements.topCollarCurveEndCtrlDx, 0), topCollarCurveEndBase.y + n(measurements.topCollarCurveEndCtrlDy, 0));
  const roundedCollarChordMid = midpoint(CP2_M, CP1_M);
  const roundedCollarNormal = normalize(
    { x: CP0_M.x - roundedCollarChordMid.x, y: CP0_M.y - roundedCollarChordMid.y },
    choosePerpendicularTowardPoint(collarExitTangent, roundedCollarChordMid, CP0_M),
  );
  const roundedCollarControlDepthBase = Math.max(
    0.125,
    dist(roundedCollarChordMid, CP0_M),
  );
  const roundedNotchCollarCurveCtrlBase = rp(
    roundedCollarChordMid.x + (roundedCollarNormal.x * roundedCollarControlDepthBase),
    roundedCollarChordMid.y + (roundedCollarNormal.y * roundedCollarControlDepthBase),
  );
  const roundedNotchCollarCurveCtrl = rp(
    roundedNotchCollarCurveCtrlBase.x + n(measurements.jacketRoundedNotchCollarCurveCtrlDx, 0),
    roundedNotchCollarCurveCtrlBase.y + n(measurements.jacketRoundedNotchCollarCurveCtrlDy, 0),
  );
  const roundedTopCollarFoldPreviewSourcePoints = lapelType === 'rounded_notch'
    ? [
      GP_M,
      CP1_M,
      ...Array.from({ length: 91 }, (_, index) => quadPoint(CP1_M, roundedNotchCollarCurveCtrl, CP2_M, index / 90)).slice(1),
      ...sampleCubicTailByLength(TCOL2, topCollarCurveStartCtrl, topCollarCurveEndCtrl, CP2_M, 1).points.reverse().slice(1),
    ]
    : [];
  const foldedTopCollarPreviewSample = lapelType === 'rounded_notch'
    ? { points: roundedTopCollarFoldPreviewSourcePoints, length: polylineLength(roundedTopCollarFoldPreviewSourcePoints) }
    : sampleCubicTailByLength(TCOL2, topCollarCurveStartCtrl, topCollarCurveEndCtrl, topCollarCurveEndPoint, 2);
  const foldedTopCollarPreviewPoints = foldedTopCollarPreviewSample.points.map((point) => reflectPointAcrossLine(point, RL0, RL1));
  if (lapelType === 'shawl') {
    shawlCurveCtrl1 = midpoint(
      TCOL2,
      LP0_M,
      n(measurements.shawlTcol2Lp0CtrlDx, 0),
      n(measurements.shawlTcol2Lp0CtrlDy, 0),
    );
    shawlCurveCtrl2 = midpoint(
      LP0_M,
      RL1,
      n(measurements.shawlLp0Rl1CtrlDx, 0),
      n(measurements.shawlLp0Rl1CtrlDy, 0),
    );
    shawlLapelDraft = createShawlLapelPath({
      FHPS,
      UCOL1,
      UCOL2,
      TCOL1,
      TCOL2,
      LP0_M,
      RL1,
      curveCtrl1: shawlCurveCtrl1,
      curveCtrl2: shawlCurveCtrl2,
    });
    shawlReflectedTcol2 = reflectPointAcrossLine(TCOL2, RL0, RL1);
    shawlReflectedCtrl1 = reflectPointAcrossLine(shawlCurveCtrl1, RL0, RL1);
    shawlReflectedCtrl2 = reflectPointAcrossLine(shawlCurveCtrl2, RL0, RL1);
    shawlReflectedRl1 = reflectPointAcrossLine(RL1, RL0, RL1);
    shawlFoldedPreviewPath = `M ${toSVG(shawlReflectedRl1)} C ${toSVG(shawlReflectedCtrl2)} ${toSVG(shawlReflectedCtrl1)} ${toSVG(shawlReflectedTcol2)}`;
    lapelCutOutlinePath = shawlLapelDraft.path;
    lapelConstructionLines = shawlLapelDraft.lines;
    lapelFormulaLines.push(...shawlLapelDraft.formulaLines);
  } else if (lapelType === 'diamond_shawl') {
    RL2 = rp(
      RL1.x + 3 + n(measurements.jacketRl2Dx, 0),
      RL1.y - 1 + n(measurements.jacketRl2Dy, 0),
    );
    applyResolvedPointDeltaToPoint('RL2', RL2);
    diamondShawlCurveCtrl = midpoint(
      TCOL2,
      RL2,
      n(measurements.diamondShawlTcol2Rl2CtrlDx, 0),
      n(measurements.diamondShawlTcol2Rl2CtrlDy, 0),
    );
    diamondShawlLapelDraft = createDiamondShawlLapelPath({
      FHPS,
      UCOL1,
      UCOL2,
      TCOL1,
      TCOL2,
      RL1,
      RL2,
      curveCtrl: diamondShawlCurveCtrl,
    });
    diamondShawlReflectedTcol2 = reflectPointAcrossLine(TCOL2, RL0, RL1);
    diamondShawlReflectedCtrl = reflectPointAcrossLine(diamondShawlCurveCtrl, RL0, RL1);
    diamondShawlReflectedRl2 = reflectPointAcrossLine(RL2, RL0, RL1);
    diamondShawlReflectedRl1 = reflectPointAcrossLine(RL1, RL0, RL1);
    diamondShawlFoldedPreviewPath = `M ${toSVG(diamondShawlReflectedRl1)} L ${toSVG(diamondShawlReflectedRl2)} Q ${toSVG(diamondShawlReflectedCtrl)} ${toSVG(diamondShawlReflectedTcol2)}`;
    lapelCutOutlinePath = diamondShawlLapelDraft.path;
    lapelConstructionLines = diamondShawlLapelDraft.lines;
    lapelFormulaLines.push(...diamondShawlLapelDraft.formulaLines);
  }

  if (lapelType === 'rounded_notch') {
    roundedNotchDraft = createRoundedNotchLapelPath({
      FND,
      LP0,
      RN1,
      RN2,
      RL1,
      GP,
      CP0,
      LB0,
      LB1,
      RN1_M,
      RN2_M,
      RL1_M,
      GP_M,
      LB0_M,
      LB1_M,
      roundCtrl: roundedNotchCornerCtrl,
      roundCtrlMirror: roundedNotchCornerCtrlMirror,
      fallCtrl: roundedNotchLapelFallCtrl,
      fallCtrlMirror: roundedNotchLapelFallCtrlMirror,
      collarEntry: CP2_M,
      collarExit: CP1_M,
      collarRoundCtrl: roundedNotchCollarCurveCtrl,
    });
    lapelCutOutlinePath = roundedNotchDraft.path;
    lapelConstructionLines = roundedNotchDraft.lines;
    lapelFormulaLines.push(...roundedNotchDraft.formulaLines);
  }

  const smoothFrontArmholeDefaults = !hasAnyNonZero([
    'frontArmPart1CtrlDx', 'frontArmPart1CtrlDy',
    'frontArmPart2CtrlDx', 'frontArmPart2CtrlDy',
  ]);
  const smoothBackArmholeDefaults = !hasAnyNonZero([
    'backArmPart1CtrlDx', 'backArmPart1CtrlDy',
    'backArmPart2CtrlDx', 'backArmPart2CtrlDy',
  ]);
  const smoothFrontSideDefaults = !hasAnyNonZero([
    'frontChestWaistCtrlDx', 'frontChestWaistCtrlDy',
    'frontWaistBellyCtrlDx', 'frontWaistBellyCtrlDy',
    'frontBellySeatCtrlDx', 'frontBellySeatCtrlDy',
  ]);
  const smoothBackSideDefaults = !hasAnyNonZero([
    'backChestWaistCtrlDx', 'backChestWaistCtrlDy',
    'backWaistBellyCtrlDx', 'backWaistBellyCtrlDy',
    'backBellySeatCtrlDx', 'backBellySeatCtrlDy',
  ]);
  const smoothSideFrontPanelDefaults = !hasAnyNonZero([
    'sideFrontArmCtrlDx', 'sideFrontArmCtrlDy',
    'sideFrontChestWaistCtrlDx', 'sideFrontChestWaistCtrlDy',
    'sideFrontWaistBellyCtrlDx', 'sideFrontWaistBellyCtrlDy',
    'sideFrontBellySeatCtrlDx', 'sideFrontBellySeatCtrlDy',
  ]);
  const smoothSideBackPanelDefaults = !hasAnyNonZero([
    'sideBackArmCtrlDx', 'sideBackArmCtrlDy',
    'sideBackChestWaistCtrlDx', 'sideBackChestWaistCtrlDy',
    'sideBackWaistBellyCtrlDx', 'sideBackWaistBellyCtrlDy',
    'sideBackBellySeatCtrlDx', 'sideBackBellySeatCtrlDy',
  ]);

  // Smooth defaults for generated jacket geometry:
  // enforce tangent-continuous handles at split joins so armholes and side seams
  // are smooth out-of-the-box while still allowing later manual control edits.
  {
    if (smoothFrontArmholeDefaults) {
      const frontArmSmooth = smoothTurnPoint({
        prevPoint: resolved.FLPS,
        turnPoint: resolved.FXF,
        nextPoint: FCPS,
        incomingControl: frontArmPart1Ctrl,
        outgoingControl: frontArmPart2Ctrl,
      });
      frontArmPart1Ctrl = frontArmSmooth.incomingControl;
      frontArmPart2Ctrl = frontArmSmooth.outgoingControl;
    }

    if (smoothBackArmholeDefaults) {
      const backArmSmooth = smoothTurnPoint({
        prevPoint: resolved.BLPS,
        turnPoint: resolved.BXB,
        nextPoint: BAHP,
        incomingControl: backArmPart1Ctrl,
        outgoingControl: backArmPart2Ctrl,
      });
      backArmPart1Ctrl = backArmSmooth.incomingControl;
      backArmPart2Ctrl = backArmSmooth.outgoingControl;
    }

    if (smoothFrontSideDefaults) {
      const frontSideWaist = smoothTurnPoint({
        prevPoint: FCPS,
        turnPoint: resolved.FWPS,
        nextPoint: resolved.FBPS,
        incomingControl: frontChestWaistCtrl,
        outgoingControl: frontWaistBellyCtrl,
      });
      frontChestWaistCtrl = frontSideWaist.incomingControl;
      frontWaistBellyCtrl = frontSideWaist.outgoingControl;

      const frontSideBelly = smoothTurnPoint({
        prevPoint: resolved.FWPS,
        turnPoint: resolved.FBPS,
        nextPoint: resolved.FSPS,
        incomingControl: frontWaistBellyCtrl,
        outgoingControl: frontBellySeatCtrl,
      });
      frontWaistBellyCtrl = frontSideBelly.incomingControl;
      frontBellySeatCtrl = frontSideBelly.outgoingControl;
    }

    if (smoothBackSideDefaults) {
      const backSideWaist = smoothTurnPoint({
        prevPoint: BCPS,
        turnPoint: resolved.BWPS,
        nextPoint: resolved.BBPS,
        incomingControl: backChestWaistCtrl,
        outgoingControl: backWaistBellyCtrl,
      });
      backChestWaistCtrl = backSideWaist.incomingControl;
      backWaistBellyCtrl = backSideWaist.outgoingControl;

      const backSideBelly = smoothTurnPoint({
        prevPoint: resolved.BWPS,
        turnPoint: resolved.BBPS,
        nextPoint: resolved.BSPS,
        incomingControl: backWaistBellyCtrl,
        outgoingControl: backBellySeatCtrl,
      });
      backWaistBellyCtrl = backSideBelly.incomingControl;
      backBellySeatCtrl = backSideBelly.outgoingControl;
    }

    if (smoothSideFrontPanelDefaults) {
      const sideFrontChest = smoothTurnPoint({
        prevPoint: SAHP,
        turnPoint: SCPF,
        nextPoint: SWPF,
        incomingControl: midpoint(SAHP, SCPF),
        outgoingControl: sideFrontChestWaistCtrl,
      });
      sideFrontChestWaistCtrl = sideFrontChest.outgoingControl;

      const sideFrontWaist = smoothTurnPoint({
        prevPoint: SCPF,
        turnPoint: SWPF,
        nextPoint: SBPF,
        incomingControl: sideFrontChestWaistCtrl,
        outgoingControl: sideFrontWaistBellyCtrl,
      });
      // Preserve chest→waist control Y-axis intent when user offsets are non-zero,
      // while still smoothing untouched defaults.
      const preserveSideFrontChestWaistY = !isNearZero(measurements.sideFrontChestWaistCtrlDy);
      sideFrontChestWaistCtrl = preserveSideFrontChestWaistY
        ? rp(sideFrontWaist.incomingControl.x, sideFrontChestWaistCtrl.y)
        : sideFrontWaist.incomingControl;
      sideFrontWaistBellyCtrl = sideFrontWaist.outgoingControl;

      const sideFrontBelly = smoothTurnPoint({
        prevPoint: SWPF,
        turnPoint: SBPF,
        nextPoint: SSPF,
        incomingControl: sideFrontWaistBellyCtrl,
        outgoingControl: sideFrontBellySeatCtrl,
      });
      sideFrontWaistBellyCtrl = sideFrontBelly.incomingControl;
      sideFrontBellySeatCtrl = sideFrontBelly.outgoingControl;
    }

    if (smoothSideBackPanelDefaults) {
      const sideBackChest = smoothTurnPoint({
        prevPoint: SAHP,
        turnPoint: SCPB,
        nextPoint: SWPB,
        incomingControl: sideBackArmCtrl,
        outgoingControl: sideBackChestWaistCtrl,
      });
      sideBackArmCtrl = sideBackChest.incomingControl;
      sideBackChestWaistCtrl = sideBackChest.outgoingControl;

      const sideBackWaist = smoothTurnPoint({
        prevPoint: SCPB,
        turnPoint: SWPB,
        nextPoint: SBPB,
        incomingControl: sideBackChestWaistCtrl,
        outgoingControl: sideBackWaistBellyCtrl,
      });
      sideBackChestWaistCtrl = sideBackWaist.incomingControl;
      sideBackWaistBellyCtrl = sideBackWaist.outgoingControl;

      const sideBackBelly = smoothTurnPoint({
        prevPoint: SWPB,
        turnPoint: SBPB,
        nextPoint: SSPB,
        incomingControl: sideBackWaistBellyCtrl,
        outgoingControl: sideBackBellySeatCtrl,
      });
      sideBackWaistBellyCtrl = sideBackBelly.incomingControl;
      sideBackBellySeatCtrl = sideBackBelly.outgoingControl;
    }
  }

  const targetFrontSideSeamLength = cubicLength(
    FCPS,
    frontChestWaistCtrl,
    frontWaistBellyCtrl,
    frontBellySeatCtrl,
    resolved.FSPS,
  );
  const resolvedSspfY = solveSideHemYForTargetSeamLength({
    startPoint: SCPF,
    control1: sideFrontChestWaistCtrl,
    control2: sideFrontWaistBellyCtrl,
    control3: sideFrontBellySeatCtrl,
    targetEnd: SSPF,
    targetLength: targetFrontSideSeamLength,
    x: SSPF.x,
  });
  SSPF = rp(SSPF.x, resolved.FSPS.y);
  SSPB = rp(SSPB.x, resolved.BSPS.y);
  sideFrontBellySeatCtrl = midpoint(SBPF, SSPF, n(measurements.sideFrontBellySeatCtrlDx, 0), n(measurements.sideFrontBellySeatCtrlDy, 0));
  sideBackBellySeatCtrl = midpoint(SBPB, SSPB, n(measurements.sideBackBellySeatCtrlDx, 0), n(measurements.sideBackBellySeatCtrlDy, 0));
  const frontShoulderCtrl = midpoint(FHPS, resolved.FLPS, n(measurements.frontShoulderCurveCtrlDx, 0), n(measurements.frontShoulderCurveCtrlDy, 0));
  const backShoulderCtrl = midpoint(BHPS, resolved.BLPS, n(measurements.backShoulderCurveCtrlDx, 0), n(measurements.backShoulderCurveCtrlDy, 0));
  const frontLapelMirrorLeadSegment = lapelType === 'peak'
    ? `L ${toSVG(LP2_M)} L ${toSVG(LP3_M)}`
    : (lapelType === 'rounded_notch'
      ? `L ${toSVG(RN1_M)} Q ${toSVG(roundedNotchCornerCtrlMirror)} ${toSVG(RN2_M)}`
      : `L ${toSVG(LP0_M)}`);
  const frontLapelMirrorSegment = lapelType === 'pointed_notch'
    ? `Q ${toSVG(pointedLapelFallCtrlMirror)} ${toSVG(RL1)}`
    : (lapelType === 'rounded_notch'
      ? `Q ${toSVG(roundedNotchLapelFallCtrlMirror)} ${toSVG(RL1_M)}`
      : `Q ${toSVG(peakLapelFallCtrlMirror)} ${toSVG(RL1_M)}`);
  const frontUpperContourSegment = lapelType === 'shawl'
    ? `L ${toSVG(UCOL1)} L ${toSVG(UCOL2)} L ${toSVG(TCOL1)} L ${toSVG(TCOL2)} C ${toSVG(shawlCurveCtrl1)} ${toSVG(shawlCurveCtrl2)} ${toSVG(RL1)}`
    : (lapelType === 'diamond_shawl'
      ? `L ${toSVG(UCOL1)} L ${toSVG(UCOL2)} L ${toSVG(TCOL1)} L ${toSVG(TCOL2)} Q ${toSVG(diamondShawlCurveCtrl)} ${toSVG(RL2)} L ${toSVG(RL1)}`
      : `Q ${toSVG(frontNeckCtrl)} ${toSVG(FND)} ${frontLapelMirrorLeadSegment} ${frontLapelMirrorSegment}`);
  const frontClosureEdgeSegment = isDoubleBreasted
    ? `L ${toSVG(EB)} L ${toSVG(SP1)}`
    : `L ${toSVG(EB)} Q ${toSVG(ebSp1Ctrl)} ${toSVG(SP1)}`;

  const frontSideSeamPathSegment = bellyFishDartEnabled
    ? `Q ${toSVG(frontBellySeatCtrl)} ${toSVG(resolved.FBPS)} L ${toSVG(BD1)} L ${toSVG(FD4)} L ${toSVG(FD2)} L ${toSVG(FD0)} L ${toSVG(FD1)} L ${toSVG(FD3)} L ${toSVG(BD2)} L ${toSVG(resolved.FWPS)} Q ${toSVG(frontChestWaistCtrl)} ${toSVG(FCPS)}`
    : `Q ${toSVG(frontBellySeatCtrl)} ${toSVG(resolved.FBPS)} Q ${toSVG(frontWaistBellyCtrl)} ${toSVG(resolved.FWPS)} Q ${toSVG(frontChestWaistCtrl)} ${toSVG(FCPS)}`;

  const frontPath = `
    M ${toSVG(FHPS)}
    ${frontUpperContourSegment}
    ${frontClosureEdgeSegment}
    L ${toSVG(resolved.FSPS)}
    ${frontSideSeamPathSegment}
    Q ${toSVG(frontArmPart2Ctrl)} ${toSVG(resolved.FXF)}
    Q ${toSVG(frontArmPart1Ctrl)} ${toSVG(resolved.FLPS)}
    Q ${toSVG(frontShoulderCtrl)} ${toSVG(FHPS)}
    Z
  `.replace(/\s+/g, ' ').trim();

  const buildCubicPathFromPoints = (points) => {
    const extended = [points[0], ...points, points[points.length - 1]];
    const cubicSegments = [];

    for (let i = 1; i < extended.length - 2; i += 1) {
      const p0 = extended[i - 1];
      const p1 = extended[i];
      const p2 = extended[i + 1];
      const p3 = extended[i + 2];
      const { c1, c2 } = catmullRomToBezier(p0, p1, p2, p3, 1);

      cubicSegments.push({
        start: p1,
        c1,
        c2,
        end: p2,
      });
    }

    return cubicSegments
      .map((segment, index) => {
        if (index === 0) {
          return `M ${toSVG(segment.start)} C ${toSVG(segment.c1)} ${toSVG(segment.c2)} ${toSVG(segment.end)}`;
        }
        return `C ${toSVG(segment.c1)} ${toSVG(segment.c2)} ${toSVG(segment.end)}`;
      })
      .join(' ');
  };

  const centerBackForwardPoints = [CB1, CB2, CB3Resolved, resolved.CB4, CB5, CB6, CB7];
  const centerBackCurveD = buildCubicPathFromPoints(centerBackForwardPoints);
  const centerBackReverseCurveD = buildCubicPathFromPoints([...centerBackForwardPoints].reverse());

  const centerBackPathChain = centerBackReverseCurveD
    .replace(/^M\s[^C]*C\s*/, '')
    .trim();

  const backPath = `
    M ${toSVG(CB1)}
    Q ${toSVG(backNeckCtrl)} ${toSVG(BHPS)}
    Q ${toSVG(backShoulderCtrl)} ${toSVG(resolved.BLPS)}
    Q ${toSVG(backArmPart1Ctrl)} ${toSVG(resolved.BXB)}
    Q ${toSVG(backArmPart2Ctrl)} ${toSVG(BAHP)}
    L ${toSVG(BCPS)}
    Q ${toSVG(backChestWaistCtrl)} ${toSVG(resolved.BWPS)}
    Q ${toSVG(backWaistBellyCtrl)} ${toSVG(resolved.BBPS)}
    Q ${toSVG(backBellySeatCtrl)} ${toSVG(resolved.BSPS)}
    L ${toSVG(CB7)}
    ${centerBackPathChain}
    Z
  `.replace(/\s+/g, ' ').trim();

  const sidePath = `
    M ${toSVG(SAHP)}
    Q ${toSVG(sideFrontArmCtrl)} ${toSVG(SCPF)}
    Q ${toSVG(sideFrontChestWaistCtrl)} ${toSVG(SWPF)}
    Q ${toSVG(sideFrontWaistBellyCtrl)} ${toSVG(SBPF)}
    Q ${toSVG(sideFrontBellySeatCtrl)} ${toSVG(SSPF)}
    L ${toSVG(SSPB)}
    Q ${toSVG(sideBackBellySeatCtrl)} ${toSVG(SBPB)}
    Q ${toSVG(sideBackWaistBellyCtrl)} ${toSVG(SWPB)}
    Q ${toSVG(sideBackChestWaistCtrl)} ${toSVG(SCPB)}
    Q ${toSVG(sideBackArmCtrl)} ${toSVG(SAHP)}
    Z
  `.replace(/\s+/g, ' ').trim();

  const curveSegments = [
    { id: 'back-neck-curve', label: 'Back Neck Curve (CB1→BHPS)', d: `M ${toSVG(CB1)} Q ${toSVG(backNeckCtrl)} ${toSVG(BHPS)}`, color: '#991b1b' },
    { id: 'front-neck-curve', label: 'Front Neck Curve (FHPS→FND)', d: `M ${toSVG(FHPS)} Q ${toSVG(frontNeckCtrl)} ${toSVG(FND)}`, color: '#1d4ed8' },
    { id: 'back-shoulder-seam-curve', label: 'Back Shoulder Seam (BHPS→BLPS)', d: `M ${toSVG(BHPS)} Q ${toSVG(backShoulderCtrl)} ${toSVG(resolved.BLPS)}`, color: '#991b1b' },
    { id: 'front-shoulder-seam-curve', label: 'Front Shoulder Seam (FLPS→FHPS)', d: `M ${toSVG(resolved.FLPS)} Q ${toSVG(frontShoulderCtrl)} ${toSVG(FHPS)}`, color: '#0f766e' },
    ...(lapelType === 'pointed_notch' ? [{ id: 'front-lapel-fall-curve', label: 'Lapel Fall Curve (LP0→RL1)', d: `M ${toSVG(LP0)} Q ${toSVG(pointedLapelFallCtrl)} ${toSVG(RL1)}`, color: '#0f172a' }] : []),
    ...(lapelType === 'rounded_notch' ? [
      { id: 'front-lapel-fall-curve', label: 'Lapel Fall Curve (RN2→RL1)', d: `M ${toSVG(RN2)} Q ${toSVG(roundedNotchLapelFallCtrl)} ${toSVG(RL1)}`, color: '#0f172a' },
      { id: 'front-rounded-notch-curve', label: 'Rounded Notch Curve (RN1→RN2)', d: `M ${toSVG(RN1)} Q ${toSVG(roundedNotchCornerCtrl)} ${toSVG(RN2)}`, color: '#0f766e' },
    ] : []),
    ...(lapelType === 'peak' ? [{ id: 'front-peak-lapel-fall-curve', label: 'Peak Lapel Fall Curve (LP3→RL1)', d: `M ${toSVG(LP3)} Q ${toSVG(peakLapelFallCtrl)} ${toSVG(RL1)}`, color: '#0f172a' }] : []),
    ...(lapelType === 'shawl' ? [
      { id: 'front-shawl-tcol2-rl1-curve', label: 'Shawl Contour (TCOL2→RL1)', d: `M ${toSVG(TCOL2)} C ${toSVG(shawlCurveCtrl1)} ${toSVG(shawlCurveCtrl2)} ${toSVG(RL1)}`, color: '#0f172a' },
    ] : []),
    ...(lapelType === 'diamond_shawl' ? [
      { id: 'front-diamond-shawl-tcol2-rl2-curve', label: 'Diamond Shawl Contour (TCOL2→RL2)', d: `M ${toSVG(TCOL2)} Q ${toSVG(diamondShawlCurveCtrl)} ${toSVG(RL2)}`, color: '#0f172a' },
    ] : []),
    ...(!isShawlFamilyLapelType(lapelType) ? [
      { id: 'front-collar-undercollar-curve', label: 'Undercollar Curve (UCOL2→FND)', d: `M ${toSVG(UCOL2)} C ${toSVG(underCollarCurveStartCtrl)} ${toSVG(underCollarCurveEndCtrl)} ${toSVG(FND)}`, color: '#0f172a' },
      { id: 'front-collar-top-collar-curve', label: lapelType === 'rounded_notch' ? 'Top Collar Curve (TCOL2→CP2_M)' : 'Top Collar Curve (TCOL2→CP0_M)', d: `M ${toSVG(TCOL2)} C ${toSVG(topCollarCurveStartCtrl)} ${toSVG(topCollarCurveEndCtrl)} ${toSVG(topCollarCurveEndPoint)}`, color: '#475569' },
      ...(lapelType === 'rounded_notch' ? [{ id: 'front-collar-cp0-round-curve', label: 'Collar Round Curve (CP2_M→CP0_M→CP1_M)', d: `M ${toSVG(CP2_M)} Q ${toSVG(roundedNotchCollarCurveCtrl)} ${toSVG(CP1_M)}`, color: '#64748b' }] : []),
    ] : []),
    { id: 'front-armhole-curve-part-1', label: 'Front Armhole Part 1 (FLPS→FXF)', d: `M ${toSVG(resolved.FLPS)} Q ${toSVG(frontArmPart1Ctrl)} ${toSVG(resolved.FXF)}`, color: '#1d4ed8' },
    { id: 'front-armhole-curve-part-2', label: 'Front Armhole Part 2 (FXF→FCPS)', d: `M ${toSVG(resolved.FXF)} Q ${toSVG(frontArmPart2Ctrl)} ${toSVG(FCPS)}`, color: '#1d4ed8' },
    ...(!isDoubleBreasted ? [{ id: 'front-hem-curve', label: 'Front Hem Curve (EB→SP1)', d: `M ${toSVG(EB)} Q ${toSVG(ebSp1Ctrl)} ${toSVG(SP1)}`, color: '#0f766e' }] : []),
    { id: 'front-side-seam-upper-curve', label: 'Front Side Seam Upper', d: bellyFishDartEnabled ? `M ${toSVG(FCPS)} Q ${toSVG(frontChestWaistCtrl)} ${toSVG(resolved.FWPS)} L ${toSVG(BD2)}` : `M ${toSVG(FCPS)} Q ${toSVG(frontChestWaistCtrl)} ${toSVG(resolved.FWPS)}`, color: '#1e40af' },
    { id: 'front-side-seam-lower-curve', label: 'Front Side Seam Lower', d: bellyFishDartEnabled ? `M ${toSVG(BD1)} Q ${toSVG(frontWaistBellyCtrl)} ${toSVG(resolved.FBPS)} Q ${toSVG(frontBellySeatCtrl)} ${toSVG(resolved.FSPS)}` : `M ${toSVG(resolved.FWPS)} Q ${toSVG(frontWaistBellyCtrl)} ${toSVG(resolved.FBPS)} Q ${toSVG(frontBellySeatCtrl)} ${toSVG(resolved.FSPS)}`, color: '#1e40af' },
    { id: 'back-armhole-curve-part-1', label: 'Back Armhole Part 1 (BLPS→BXB)', d: `M ${toSVG(resolved.BLPS)} Q ${toSVG(backArmPart1Ctrl)} ${toSVG(resolved.BXB)}`, color: '#b91c1c' },
    { id: 'back-armhole-curve-part-2', label: 'Back Armhole Part 2 (BXB→BAHP)', d: `M ${toSVG(resolved.BXB)} Q ${toSVG(backArmPart2Ctrl)} ${toSVG(BAHP)}`, color: '#b91c1c' },
    { id: 'back-center-line-curve', label: 'Center Back Curve (CB1→CB7)', d: centerBackCurveD, color: '#991b1b' },
    { id: 'back-side-seam-curve', label: 'Back Side Seam', d: `M ${toSVG(BCPS)} Q ${toSVG(backChestWaistCtrl)} ${toSVG(resolved.BWPS)} Q ${toSVG(backWaistBellyCtrl)} ${toSVG(resolved.BBPS)} Q ${toSVG(backBellySeatCtrl)} ${toSVG(resolved.BSPS)}`, color: '#b91c1c' },
    { id: 'side-front-arm-curve', label: 'Side Front Arm Curve', d: `M ${toSVG(SAHP)} Q ${toSVG(sideFrontArmCtrl)} ${toSVG(SCPF)}`, color: '#9a3412' },
    { id: 'side-front-seam-curve', label: 'Side Front Seam', d: `M ${toSVG(SCPF)} Q ${toSVG(sideFrontChestWaistCtrl)} ${toSVG(SWPF)} Q ${toSVG(sideFrontWaistBellyCtrl)} ${toSVG(SBPF)} Q ${toSVG(sideFrontBellySeatCtrl)} ${toSVG(SSPF)}`, color: '#9a3412' },
    { id: 'side-back-seam-curve', label: 'Side Back Seam', d: `M ${toSVG(SAHP)} Q ${toSVG(sideBackArmCtrl)} ${toSVG(SCPB)} Q ${toSVG(sideBackChestWaistCtrl)} ${toSVG(SWPB)} Q ${toSVG(sideBackWaistBellyCtrl)} ${toSVG(SBPB)} Q ${toSVG(sideBackBellySeatCtrl)} ${toSVG(SSPB)}`, color: '#92400e' },
  ];

  const createHandle = (id, curveId, label, point, segmentStart, segmentEnd, xKey, yKey, color, defaults = {}) => ({
    id,
    curveId,
    label,
    point,
    segmentStart,
    segmentEnd,
    xKey,
    yKey,
    color,
    defaultDx: defaults.defaultDx ?? 0,
    defaultDy: defaults.defaultDy ?? 0,
  });

  const curveHandles = [
    createHandle('back-neck-ctrl', 'back-neck-curve', 'Back Neck Control', backNeckCtrl, CB1, BHPS, 'backNeckCtrlDx', 'backNeckCtrlDy', '#991b1b'),
    createHandle('front-neck-ctrl', 'front-neck-curve', 'Front Neck Control', frontNeckCtrl, FHPS, FND, 'frontNeckCtrlDx', 'frontNeckCtrlDy', '#1d4ed8'),
    createHandle('back-shoulder-ctrl', 'back-shoulder-seam-curve', 'Back Shoulder Curve Control', backShoulderCtrl, BHPS, resolved.BLPS, 'backShoulderCurveCtrlDx', 'backShoulderCurveCtrlDy', '#991b1b'),
    createHandle('front-shoulder-ctrl', 'front-shoulder-seam-curve', 'Front Shoulder Curve Control', frontShoulderCtrl, resolved.FLPS, FHPS, 'frontShoulderCurveCtrlDx', 'frontShoulderCurveCtrlDy', '#0f766e'),
    ...(lapelType === 'pointed_notch' ? [createHandle('lapel-fall-ctrl', 'front-lapel-fall-curve', 'Lapel Fall Control (LP0→RL1)', pointedLapelFallCtrl, LP0, RL1, 'jacketLapelFallCurveCtrlDx', 'jacketLapelFallCurveCtrlDy', '#0f172a')] : []),
    ...(lapelType === 'rounded_notch' ? [
      createHandle('lapel-fall-ctrl', 'front-lapel-fall-curve', 'Lapel Fall Control (RN2→RL1)', roundedNotchLapelFallCtrl, RN2, RL1, 'jacketLapelFallCurveCtrlDx', 'jacketLapelFallCurveCtrlDy', '#0f172a'),
      createHandle('rounded-notch-ctrl', 'front-rounded-notch-curve', 'Rounded Notch Control (RN1→RN2)', roundedNotchCornerCtrl, RN1, RN2, 'jacketRoundedNotchCurveCtrlDx', 'jacketRoundedNotchCurveCtrlDy', '#0f766e'),
    ] : []),
    ...(lapelType === 'peak' ? [createHandle('peak-lapel-fall-ctrl', 'front-peak-lapel-fall-curve', 'Peak Lapel Fall Control (LP3→RL1)', peakLapelFallCtrl, LP3, RL1, 'jacketPeakLapelFallCurveCtrlDx', 'jacketPeakLapelFallCurveCtrlDy', '#0f172a')] : []),
    ...(lapelType === 'shawl' ? [
      createHandle('shawl-curve-ctrl-1', 'front-shawl-tcol2-rl1-curve', 'Shawl Curve Control 1', shawlCurveCtrl1, TCOL2, RL1, 'shawlTcol2Lp0CtrlDx', 'shawlTcol2Lp0CtrlDy', '#0f172a'),
      createHandle('shawl-curve-ctrl-2', 'front-shawl-tcol2-rl1-curve', 'Shawl Curve Control 2', shawlCurveCtrl2, TCOL2, RL1, 'shawlLp0Rl1CtrlDx', 'shawlLp0Rl1CtrlDy', '#0f766e'),
    ] : []),
    ...(lapelType === 'diamond_shawl' ? [
      createHandle('diamond-shawl-curve-ctrl', 'front-diamond-shawl-tcol2-rl2-curve', 'Diamond Shawl Curve Control (TCOL2→RL2)', diamondShawlCurveCtrl, TCOL2, RL2, 'diamondShawlTcol2Rl2CtrlDx', 'diamondShawlTcol2Rl2CtrlDy', '#0f172a'),
    ] : []),
    ...(!isShawlFamilyLapelType(lapelType) ? [
      createHandle('under-collar-start-ctrl', 'front-collar-undercollar-curve', 'Undercollar Start Control', underCollarCurveStartCtrl, UCOL2, FND, 'underCollarCurveStartCtrlDx', 'underCollarCurveStartCtrlDy', '#0f172a'),
      createHandle('under-collar-end-ctrl', 'front-collar-undercollar-curve', 'Undercollar End Control', underCollarCurveEndCtrl, UCOL2, FND, 'underCollarCurveEndCtrlDx', 'underCollarCurveEndCtrlDy', '#0f172a'),
      createHandle('top-collar-start-ctrl', 'front-collar-top-collar-curve', 'Top Collar Start Control', topCollarCurveStartCtrl, TCOL2, topCollarCurveEndPoint, 'topCollarCurveStartCtrlDx', 'topCollarCurveStartCtrlDy', '#475569'),
      createHandle('top-collar-end-ctrl', 'front-collar-top-collar-curve', 'Top Collar End Control', topCollarCurveEndCtrl, TCOL2, topCollarCurveEndPoint, 'topCollarCurveEndCtrlDx', 'topCollarCurveEndCtrlDy', '#475569'),
      ...(lapelType === 'rounded_notch' ? [createHandle('collar-cp0-round-ctrl', 'front-collar-cp0-round-curve', 'Collar Round Control (CP2_M→CP0_M→CP1_M)', roundedNotchCollarCurveCtrl, CP2_M, CP1_M, 'jacketRoundedNotchCollarCurveCtrlDx', 'jacketRoundedNotchCollarCurveCtrlDy', '#64748b')] : []),
    ] : []),
    createHandle('front-arm-part-1-ctrl', 'front-armhole-curve-part-1', 'Front Armhole Part 1 Control', frontArmPart1Ctrl, resolved.FLPS, resolved.FXF, 'frontArmPart1CtrlDx', 'frontArmPart1CtrlDy', '#1d4ed8'),
    createHandle('front-arm-part-2-ctrl', 'front-armhole-curve-part-2', 'Front Armhole Part 2 Control', frontArmPart2Ctrl, resolved.FXF, FCPS, 'frontArmPart2CtrlDx', 'frontArmPart2CtrlDy', '#1d4ed8'),
    ...(!isDoubleBreasted ? [createHandle('front-hem-curve-ctrl', 'front-hem-curve', 'Front Hem Curve Handle (Midpoint 50%)', ebSp1Ctrl, EB, SP1, 'frontJacketHemCurveCtrlDx', 'frontJacketHemCurveCtrlDy', '#0f766e', { defaultDx: 1.5, defaultDy: 4.5 })] : []),

    createHandle('back-arm-part-1-ctrl', 'back-armhole-curve-part-1', 'Back Armhole Part 1 Control', backArmPart1Ctrl, resolved.BLPS, resolved.BXB, 'backArmPart1CtrlDx', 'backArmPart1CtrlDy', '#b91c1c'),
    createHandle('back-arm-part-2-ctrl', 'back-armhole-curve-part-2', 'Back Armhole Part 2 Control', backArmPart2Ctrl, resolved.BXB, BAHP, 'backArmPart2CtrlDx', 'backArmPart2CtrlDy', '#b91c1c'),
    createHandle('front-cw-ctrl', 'front-side-seam-upper-curve', 'Front Chest→Waist Control', frontChestWaistCtrl, FCPS, bellyFishDartEnabled ? BD2 : resolved.FWPS, 'frontChestWaistCtrlDx', 'frontChestWaistCtrlDy', '#1e40af'),
    createHandle('front-wb-ctrl', 'front-side-seam-lower-curve', 'Front Waist→Belly Control', frontWaistBellyCtrl, bellyFishDartEnabled ? BD1 : resolved.FWPS, resolved.FBPS, 'frontWaistBellyCtrlDx', 'frontWaistBellyCtrlDy', '#1e40af'),
    createHandle('front-bs-ctrl', 'front-side-seam-lower-curve', 'Front Belly→Seat Control', frontBellySeatCtrl, resolved.FBPS, resolved.FSPS, 'frontBellySeatCtrlDx', 'frontBellySeatCtrlDy', '#1e40af'),
    createHandle('back-cw-ctrl', 'back-side-seam-curve', 'Back Chest→Waist Control', backChestWaistCtrl, BCPS, resolved.BWPS, 'backChestWaistCtrlDx', 'backChestWaistCtrlDy', '#b91c1c'),
    createHandle('back-wb-ctrl', 'back-side-seam-curve', 'Back Waist→Belly Control', backWaistBellyCtrl, resolved.BWPS, resolved.BBPS, 'backWaistBellyCtrlDx', 'backWaistBellyCtrlDy', '#b91c1c'),
    createHandle('back-bs-ctrl', 'back-side-seam-curve', 'Back Belly→Seat Control', backBellySeatCtrl, resolved.BBPS, resolved.BSPS, 'backBellySeatCtrlDx', 'backBellySeatCtrlDy', '#b91c1c'),
    createHandle('side-front-arm-ctrl', 'side-front-arm-curve', 'Side Front Arm Control', sideFrontArmCtrl, SAHP, SCPF, 'sideFrontArmCtrlDx', 'sideFrontArmCtrlDy', '#9a3412', { defaultDx: -0.75, defaultDy: 1.375 }),
    createHandle('side-front-cw-ctrl', 'side-front-seam-curve', 'Side Front Chest→Waist Control', sideFrontChestWaistCtrl, SCPF, SWPF, 'sideFrontChestWaistCtrlDx', 'sideFrontChestWaistCtrlDy', '#9a3412'),
    createHandle('side-front-wb-ctrl', 'side-front-seam-curve', 'Side Front Waist→Belly Control', sideFrontWaistBellyCtrl, SWPF, SBPF, 'sideFrontWaistBellyCtrlDx', 'sideFrontWaistBellyCtrlDy', '#9a3412'),
    createHandle('side-front-bs-ctrl', 'side-front-seam-curve', 'Side Front Belly→Seat Control', sideFrontBellySeatCtrl, SBPF, SSPF, 'sideFrontBellySeatCtrlDx', 'sideFrontBellySeatCtrlDy', '#9a3412'),
    createHandle('side-back-arm-ctrl', 'side-back-seam-curve', 'Side Back Arm Control', sideBackArmCtrl, SAHP, SCPB, 'sideBackArmCtrlDx', 'sideBackArmCtrlDy', '#92400e'),
    createHandle('side-back-cw-ctrl', 'side-back-seam-curve', 'Side Back Chest→Waist Control', sideBackChestWaistCtrl, SCPB, SWPB, 'sideBackChestWaistCtrlDx', 'sideBackChestWaistCtrlDy', '#92400e'),
    createHandle('side-back-wb-ctrl', 'side-back-seam-curve', 'Side Back Waist→Belly Control', sideBackWaistBellyCtrl, SWPB, SBPB, 'sideBackWaistBellyCtrlDx', 'sideBackWaistBellyCtrlDy', '#92400e'),
    createHandle('side-back-bs-ctrl', 'side-back-seam-curve', 'Side Back Belly→Seat Control', sideBackBellySeatCtrl, SBPB, SSPB, 'sideBackBellySeatCtrlDx', 'sideBackBellySeatCtrlDy', '#92400e'),
  ];

  const frontMeasurements = [
    { p1: FCPS, p2: CF2, label: 'Front Chest', value: Number((CF2.x - FCPS.x).toFixed(3)), referenceLine: true, showLabel: false },
    { p1: resolved.FXF, p2: CF1, label: 'Across Front', value: Number((CF1.x - resolved.FXF.x).toFixed(3)), referenceLine: true, showLabel: false, offset: 0.75 },
    { p1: resolved.FLPS, p2: rp(resolved.FLPS.x, FCPS.y), label: 'Front Scye Vertical', value: Number(Math.abs(FCPS.y - resolved.FLPS.y).toFixed(3)), referenceLine: true, showLabel: false, offset: 0.75 },
    { p1: resolved.FWPS, p2: CF3, label: 'Front Waist', value: Number((CF3.x - resolved.FWPS.x).toFixed(3)), referenceLine: true, showLabel: false },
    { p1: resolved.FBPS, p2: CF4, label: 'Front Belly', value: Number((CF4.x - resolved.FBPS.x).toFixed(3)), referenceLine: true, showLabel: false },
    { p1: resolved.FSPS, p2: CF5, label: 'Front Seat', value: Number((CF5.x - resolved.FSPS.x).toFixed(3)), referenceLine: true, showLabel: false },
  ];

  const backMeasurements = [
    { p1: resolved.CB4, p2: BCPS, label: 'Back Chest', value: Number((BCPS.x - resolved.CB4.x).toFixed(3)), referenceLine: true, showLabel: false },
    { p1: CB3Resolved, p2: resolved.BXB, label: 'Across Back', value: Number((resolved.BXB.x - CB3Resolved.x).toFixed(3)), referenceLine: true, showLabel: false, offset: 0.75 },
    { p1: CB2, p2: resolved.BLPS, label: 'Back Shoulder', value: Number(dist(CB2, resolved.BLPS).toFixed(3)), referenceLine: true, showLabel: false, offset: 0.75 },
    { p1: resolved.BLPS, p2: rp(resolved.BLPS.x, BCPS.y), label: 'Back Scye Vertical', value: Number(Math.abs(BCPS.y - resolved.BLPS.y).toFixed(3)), referenceLine: true, showLabel: false, offset: 0.75 },
    { p1: CB5, p2: resolved.BWPS, label: 'Back Waist', value: Number((resolved.BWPS.x - CB5.x).toFixed(3)), referenceLine: true, showLabel: false },
    { p1: CB6, p2: resolved.BBPS, label: 'Back Belly', value: Number((resolved.BBPS.x - CB6.x).toFixed(3)), referenceLine: true, showLabel: false },
    { p1: CB7, p2: resolved.BSPS, label: 'Back Seat', value: Number((resolved.BSPS.x - CB7.x).toFixed(3)), referenceLine: true, showLabel: false },
  ];

  const sideMeasurements = [
    { p1: SCPB, p2: SCPF, label: 'Side Chest', value: Number((SCPF.x - SCPB.x).toFixed(3)), referenceLine: true, showLabel: false },
    { p1: SWPB, p2: SWPF, label: 'Side Waist', value: Number((SWPF.x - SWPB.x).toFixed(3)), referenceLine: true, showLabel: false },
    { p1: SBPB, p2: SBPF, label: 'Side Belly', value: Number((SBPF.x - SBPB.x).toFixed(3)), referenceLine: true, showLabel: false },
    { p1: SSPB, p2: SSPF, label: 'Side Seat', value: Number((SSPF.x - SSPB.x).toFixed(3)), referenceLine: true, showLabel: false },
  ];

  const rollDx = RL1.x - RL0.x;
  const rollDy = RL1.y - RL0.y;
  const rollDenom = (rollDx ** 2) + (rollDy ** 2);
  const lapelWidthLineLength = dist(LP0, LP1);
  const lapelWidthTextPoint = midpoint(LP0, LP1, -0.22, -0.18);

  const constructionLines = [
    { p1: resolved.CB4, p2: CF2, label: 'Chest' },
    { p1: CB3Resolved, p2: resolved.BXB, label: 'Across Back' },
    { p1: RL0, p2: RL1, label: 'Roll Line' },
    { p1: CB5, p2: CF3, label: 'Waist' },
    { p1: CB6, p2: CF4, label: 'Belly' },
    { p1: CB7, p2: CF5, label: 'Hem' },
  ];

  const sideConstructionLines = [
    { p1: SCPB, p2: SCPF, label: 'Side Chest' },
    { p1: SWPB, p2: SWPF, label: 'Side Waist' },
    { p1: SBPB, p2: SBPF, label: 'Side Belly' },
    { p1: SSPB, p2: SSPF, label: 'Side Seat' },
  ];

  const namedPoints = [
    { name: 'CB0', ...CB0, piece: 'back' },
    { name: 'CB1', ...CB1, piece: 'back' },
    { name: 'CB2', ...CB2, piece: 'back' },
    { name: 'CB3', ...CB3Resolved, piece: 'back' },
    { name: 'CB4', ...resolved.CB4, piece: 'back' },
    { name: 'CB5', ...CB5, piece: 'back' },
    { name: 'CB6', ...CB6, piece: 'back' },
    { name: 'CB7', ...CB7, piece: 'back' },
    { name: 'BHPS', ...BHPS, piece: 'back' },
    { name: 'BLPS', ...resolved.BLPS, piece: 'back' },
    { name: 'BXB', ...BXBResolved, piece: 'back' },
    { name: 'BAHP', ...BAHP, piece: 'back' },
    { name: 'BCPS', ...BCPS, piece: 'back' },
    { name: 'BWPS', ...resolved.BWPS, piece: 'back' },
    { name: 'BBPS', ...resolved.BBPS, piece: 'back' },
    { name: 'BSPS', ...resolved.BSPS, piece: 'back' },

    { name: 'CF0', ...CF0, piece: 'front' },
    { name: 'CF1', ...CF1, piece: 'front' },
    { name: 'CF2', ...CF2, piece: 'front' },
    { name: 'CF3', ...CF3, piece: 'front' },
    { name: 'CF4', ...CF4, piece: 'front' },
    { name: 'CF5', ...CF5, piece: 'front' },
    { name: 'FHPS', ...FHPS, piece: 'front' },
    { name: 'FLPS', ...resolved.FLPS, piece: 'front' },
    { name: 'FND', ...FND, piece: 'front' },
    { name: 'FXF', ...resolved.FXF, piece: 'front' },
    { name: 'FCPS', ...FCPS, piece: 'front' },
    { name: 'FWPS', ...resolved.FWPS, piece: 'front' },
    { name: 'FBPS', ...resolved.FBPS, piece: 'front' },
    { name: 'FSPS', ...resolved.FSPS, piece: 'front' },
    { name: 'EB', ...EB, piece: 'front' },
    { name: 'RL0', ...RL0, piece: 'front' },
    { name: 'RL1', ...RL1, piece: 'front' },
    ...(lapelType === 'diamond_shawl' ? [{ name: 'RL2', ...RL2, piece: 'front' }] : []),
    { name: 'SP1', ...SP1, piece: 'front' },
    ...(!isShawlFamilyLapelType(lapelType) ? [
      { name: 'GP', ...GP, piece: 'front', labelOffsetX: 0.24, labelOffsetY: -0.28 },
      { name: 'CP0', ...CP0, piece: 'front' },
    ] : []),
    ...(!isShawlFamilyLapelType(lapelType) ? [
      { name: 'LB0', ...LB0, piece: 'front' },
      { name: 'LB1', ...LB1, piece: 'front' },
    ] : []),
    { name: 'LP0', ...LP0, piece: 'front', showLabel: lapelType !== 'peak' },
    { name: 'LP1', ...LP1, piece: 'front' },
    ...(lapelType === 'rounded_notch' ? [
      { name: 'RN1', ...RN1, piece: 'front', selectable: false },
      { name: 'RN2', ...RN2, piece: 'front', selectable: false },
    ] : []),
    ...(lapelType === 'peak' ? [
      { name: 'LP2', ...LP2, piece: 'front', labelOffsetX: 0.16, labelOffsetY: -0.1 },
      { name: 'LP3', ...LP3, piece: 'front' },
    ] : []),
    ...(!isShawlFamilyLapelType(lapelType) ? [
      { name: 'GP_M', ...GP_M, piece: 'front', labelOffsetX: 0.24, labelOffsetY: -0.28 },
      { name: 'CP0_M', ...CP0_M, piece: 'front', selectable: false },
    ] : []),
    ...(!isShawlFamilyLapelType(lapelType) ? [
      { name: 'LB0_M', ...LB0_M, piece: 'front' },
      { name: 'LB1_M', ...LB1_M, piece: 'front' },
    ] : []),
    { name: 'LP0_M', ...LP0_M, piece: 'front', showLabel: lapelType !== 'peak' },
    ...(lapelType === 'rounded_notch' ? [
      { name: 'RN1_M', ...RN1_M, piece: 'front', selectable: false },
      { name: 'RN2_M', ...RN2_M, piece: 'front', selectable: false },
      { name: 'CP2_M', ...CP2_M, piece: 'front', selectable: false },
      { name: 'CP1_M', ...CP1_M, piece: 'front', selectable: false },
    ] : []),
    ...(lapelType === 'peak' ? [
      { name: 'LP2_M', ...LP2_M, piece: 'front', selectable: false, labelOffsetX: 0.18, labelOffsetY: 0.16 },
      { name: 'LP3_M', ...LP3_M, piece: 'front', selectable: false },
    ] : []),
    { name: 'UCOL0', ...UCOL0, piece: 'front' },
    { name: 'UCOL1', ...UCOL1, piece: 'front' },
    { name: 'UCOL2', ...UCOL2, piece: 'front' },
    { name: 'TCOL1', ...TCOL1, piece: 'front' },
    { name: 'TCOL2', ...TCOL2, piece: 'front' },
    { name: 'SAHP', ...SAHP, piece: 'side' },
    { name: 'SCPB', ...SCPB, piece: 'side' },
    { name: 'SCPF', ...SCPF, piece: 'side' },
    { name: 'SWPB', ...SWPB, piece: 'side' },
    { name: 'SWPF', ...SWPF, piece: 'side' },
    { name: 'SBPB', ...SBPB, piece: 'side' },
    { name: 'SBPF', ...SBPF, piece: 'side' },
    { name: 'SSPB', ...SSPB, piece: 'side' },
    { name: 'SSPF', ...SSPF, piece: 'side' },
  ];

  const frontFishDartLines = bellyFishDartEnabled
    ? [
        { id: 'front-belly-dart-left', p1: BD2, p2: FD3 },
        { id: 'front-belly-dart-right', p1: BD1, p2: FD4 },
      ]
    : [];

  const frontDartPath = fishOnlyDartEnabled
    ? `M ${toSVG(FD0)} L ${toSVG(FD1)} L ${toSVG(FD3)} L ${toSVG(FD2)} Z`
    : '';

  const jacketPocketMarkers = [];

  if (chestPocketEnabled && CPKT1 && CPKT2 && CPKT3 && CPKT4) {
    jacketPocketMarkers.push({
      id: 'jacket-chest-pocket-outline',
      label: 'Chest Pocket Outline',
      d: `M ${toSVG(CPKT1)} L ${toSVG(CPKT2)} L ${toSVG(CPKT3)} L ${toSVG(CPKT4)} Z`,
      stroke: '#0f172a',
      strokeWidth: 0.18,
    });
  }

  if (sidePocketEnabled && SPK1 && SPK2) {
    jacketPocketMarkers.push({
      id: 'jacket-side-pocket-line',
      label: 'Side Pocket Opening',
      d: `M ${toSVG(SPK1)} L ${toSVG(SPK2)}`,
      stroke: '#334155',
      strokeWidth: 0.2,
    });
  }

  if (bellyFishDartEnabled) {
    namedPoints.push(
      { name: 'FD0', ...FD0, piece: 'front' },
      { name: 'FD1', ...FD1, piece: 'front' },
      { name: 'FD2', ...FD2, piece: 'front' },
      { name: 'FD3', ...FD3, piece: 'front' },
      { name: 'FD4', ...FD4, piece: 'front' },
      { name: 'BD1', ...BD1, piece: 'front' },
      { name: 'BD2', ...BD2, piece: 'front' },
    );
  } else if (fishOnlyDartEnabled) {
    namedPoints.push(
      { name: 'FD0', ...FD0, piece: 'front' },
      { name: 'FD1', ...FD1, piece: 'front' },
      { name: 'FD2', ...FD2, piece: 'front' },
      { name: 'FD3', ...FD3, piece: 'front' },
    );
  }

  if (chestPocketEnabled && CPKT1 && CPKT2 && CPKT3 && CPKT4) {
    namedPoints.push(
      { name: 'CPKT1', ...CPKT1, piece: 'front' },
      { name: 'CPKT2', ...CPKT2, piece: 'front' },
      { name: 'CPKT3', ...CPKT3, piece: 'front' },
      { name: 'CPKT4', ...CPKT4, piece: 'front' },
    );
  }

  if (sidePocketEnabled && SPK1 && SPK2) {
    namedPoints.push(
      { name: 'SPK1', ...SPK1, piece: 'front' },
      { name: 'SPK2', ...SPK2, piece: 'front' },
    );
  }

  const lowerButtonDistanceSeed = n(measurements.jacketLowerButtonDistance ?? 4.25, 4.25);
  const upperButtonDistanceSeed = n(measurements.jacketUpperButtonDistance ?? 4.25, 4.25);
  const buttonEdgeOffsetSeed = n(measurements.jacketButtonEdgeOffset ?? 0.5, 0.5);
  const lowerInteriorHint = resolved.FWPS && resolved.FBPS ? midpoint(resolved.FWPS, resolved.FBPS) : CF3;
  const upperInteriorHint = GP || CF3;
  const lowerButtonBasePoint = pointOnLineAtY(RL1, EB, CF3.y + lowerButtonDistanceSeed);
  const lowerButtonSeed = offsetPointTowardInterior(lowerButtonBasePoint, RL1, EB, buttonEdgeOffsetSeed, lowerInteriorHint);
  const upperButtonBasePoint = LP0_M ? pointOnLineAtY(RL1, LP0_M, CF3.y - upperButtonDistanceSeed) : null;
  const upperButtonSeed = upperButtonBasePoint
    ? offsetPointTowardInterior(upperButtonBasePoint, RL1, LP0_M, buttonEdgeOffsetSeed, upperInteriorHint)
    : null;

  const buttonPointSeeds = {
    DB_TOP_BTN: isDoubleBreasted ? rp(RL1.x - 8, RL1.y - 4.5) : null,
    BTN_WAIST: rp(CF3.x, CF3.y),
    BTN_LOWER: lowerButtonSeed,
    BTN_ROLL: upperButtonSeed,
    DB_BTN_UPPER_EDGE: isDoubleBreasted ? rp(RL1.x - buttonEdgeOffsetSeed, RL1.y) : null,
    DB_BTN_UPPER_INNER: isDoubleBreasted ? rp(RL1.x - 4, RL1.y) : null,
    DB_BTN_LOWER_EDGE: isDoubleBreasted ? rp(RL1.x - buttonEdgeOffsetSeed, CF3.y + lowerButtonDistanceSeed) : null,
    DB_BTN_LOWER_INNER: isDoubleBreasted ? rp(RL1.x - 4, CF3.y + lowerButtonDistanceSeed) : null,
  };
  Object.entries(buttonPointSeeds).forEach(([pointName, pointValue]) => {
    if (pointValue) {
      applyResolvedPointDeltaToPoint(pointName, pointValue);
    }
  });

  const jacketButtonPlan = buildJacketFrontButtons({
    CF3,
    RL1,
    EB,
    LP0_M,
    FWPS: resolved.FWPS,
    FBPS: resolved.FBPS,
    GP,
    ...buttonPointSeeds,
  }, measurements);
  const buttons = jacketButtonPlan.buttons;
  const buttonholes = jacketButtonPlan.buttonholes;

  buttons.forEach((button, index) => {
    if (!button?.pointName) return;
    namedPoints.push({
      name: button.pointName,
      x: button.x,
      y: button.y,
      piece: 'front',
      renderNode: false,
      showLabel: false,
      label: button.pointName,
      buttonOrder: index + 1,
    });
  });

  const topCollarOutlinePath = lapelType === 'rounded_notch'
    ? `M ${toSVG(UCOL2)} L ${toSVG(TCOL1)} L ${toSVG(TCOL2)} C ${toSVG(topCollarCurveStartCtrl)} ${toSVG(topCollarCurveEndCtrl)} ${toSVG(CP2_M)} Q ${toSVG(roundedNotchCollarCurveCtrl)} ${toSVG(CP1_M)} L ${toSVG(GP_M)}`
    : `M ${toSVG(UCOL2)} L ${toSVG(TCOL1)} L ${toSVG(TCOL2)} C ${toSVG(topCollarCurveStartCtrl)} ${toSVG(topCollarCurveEndCtrl)} ${toSVG(CP0_M)}`;
  const jacketCollarPaths = isShawlFamilyLapelType(lapelType) ? [] : [
    {
      id: 'undercollar-outline',
      d: `M ${toSVG(FND)} Q ${toSVG(frontNeckCollarSegment.p1)} ${toSVG(frontNeckCutPoint)} L ${toSVG(UCOL0)} L ${toSVG(UCOL1)} L ${toSVG(UCOL2)} C ${toSVG(underCollarCurveStartCtrl)} ${toSVG(underCollarCurveEndCtrl)} ${toSVG(FND)}`,
      stroke: '#0f172a',
      strokeWidth: 0.22,
    },
    {
      id: 'top-collar-outline',
      d: topCollarOutlinePath,
      stroke: '#334155',
      strokeWidth: 0.2,
    },
    {
      id: 'folded-top-collar-preview',
      d: buildPolylinePath(foldedTopCollarPreviewPoints),
      stroke: '#0f766e',
      strokeWidth: 0.18,
      strokeDasharray: '0.18 0.1',
    },
  ];
  const jacketShawlFoldedPreviewPaths = lapelType === 'shawl'
    ? [{
      id: 'shawl-folded-lapel-preview',
      d: shawlFoldedPreviewPath,
      stroke: '#0f766e',
      strokeWidth: 0.18,
      strokeDasharray: '0.18 0.1',
    }]
    : (lapelType === 'diamond_shawl'
      ? [{
        id: 'diamond-shawl-folded-lapel-preview',
        d: diamondShawlFoldedPreviewPath,
        stroke: '#0f766e',
        strokeWidth: 0.18,
        strokeDasharray: '0.18 0.1',
      }]
      : []);

  const jacketCollarNotes = [
    {
      id: 'bc-length',
      piece: 'back',
      point: midpoint(CB1, BHPS, 0.1, -1.0),
      text: `BC: ${fmt(backNeckCurveLength)}"`,
      color: '#0f172a',
      textAnchor: 'middle',
      fontSize: '0.42',
      fontWeight: '600',
    },
    {
      id: 'collar-match-length',
      piece: 'front',
      point: midpoint(collarMatchStartPoint, UCOL1, 0.08, -0.42),
      text: `${isShawlFamilyLapelType(lapelType) ? 'FHPS→UC1' : 'UC0→UC1'}: ${fmt(collarMatchSegmentLength)}"`,
      color: '#0f172a',
      textAnchor: 'middle',
      fontSize: '0.38',
      fontWeight: '600',
    },
    {
      id: 'lapel-width',
      piece: 'front',
      point: lapelWidthTextPoint,
      text: `LW: ${fmt(lapelWidthLineLength)}"`,
      color: '#0f172a',
      textAnchor: 'middle',
      fontSize: '0.4',
      fontWeight: '600',
    },
  ];

  const jacketLapelMeasurementLines = [
    {
      id: 'lapel-width-line',
      piece: 'front',
      p1: LP0,
      p2: LP1,
      stroke: '#0f172a',
      strokeWidth: 0.14,
      strokeDasharray: '0.18 0.1',
    },
  ];

  const frontWaistRaw = CF3.x - resolved.FWPS.x;
  const frontWaistDartIntake = fishDartEnabled ? Math.abs(FD2.x - FD1.x) : 0;
  const frontWaistNet = frontWaistRaw - frontWaistDartIntake;
  const frontWaistMeasurement = frontMeasurements.find((item) => item.label === 'Front Waist');
  if (frontWaistMeasurement) {
    frontWaistMeasurement.value = Number(frontWaistNet.toFixed(3));
  }

  const pointReferences = [
    'Origin: CB0 = (0,0). +X toward center-front, +Y toward hem.',
    'CF0 vertical offset follows Bk to FT HPS exactly: CF0.y = CB0.y + backToFtHps.',
    'FHPS shares CF0 y-level. RL0 shares FHPS y-level and is placed +1" in x from FHPS.',
    'FND lies on Roll Line (RL0→RL1), and FHPS→FND is drafted as a soft front-neck curve with its own adjustable control handle.',
    'Across-Back and Across-Front set shoulder/chest split references used by BXB and FXF.',
    'X-Front depth override controls CF0→CF1 (and FXF y-level) vertical drop.',
    'X-Back depth override controls CB0→CB3/BXB vertical drop.',
    `Jacket belly profile: ${bellyProfile.replaceAll('_', ' ')} | FWPS/FBPS/FSPS = FCPS + ${Number(bellyProfileRule.frontSideOffset).toFixed(3)}" | CF3/CF4 = CF0 + ${Number(bellyProfileRule.cfForwardOffset).toFixed(3)}" in x before manual offsets | SWPF = SCPF + ${Number(bellyProfileRule.swpfOffsetFromScpf).toFixed(3)}" before manual offsets.`,
    `Jacket posture: ${String(measurements?.posture || 'normal').replaceAll('_', ' ')} | Bk to FT HPS seeds backToFtHps = ${Number(backToFtHps).toFixed(3)}" unless manually edited later.`,
    'Side panel balance follows: Side = TotalHalf - Front - Back at chest/waist/belly/seat.',
    'Front armhole is split FLPS→FXF and FXF→FCPS with independent segment controls.',
    'Back armhole is split BLPS→BXB and BXB→BAHP with independent segment controls.',
    'Jacket Length Front is resolved as vertical distance from FHPS to SP1, with SP1 vertically aligned from FD3, FSPS/CF5 sharing the front hem y-level, and SSPF iteratively resolved so the front and side seam lengths stay matched.',
    'Point XY offsets are in 1/16" steps for key points (CB4, BLPS, BXB, FLPS, FXF, BWPS, FWPS, BBPS, FBPS, BSPS, FSPS).',
    'Free Mode (when active) applies point deltas separately from measurements and does not overwrite base parametric inputs.',
    `Front dart mode: ${jacketDartMode.replaceAll('_', ' ')} | FD0/FD1/FD2 define the upper fish-dart suppression, ${bellyFishDartEnabled ? 'FD3/FD4 + BD1/BD2 add the belly cut' : 'belly cut is suppressed'}.`,
    'Shoulder seams now accept curve controls. When both control offsets are 0,0 the handle sits at the segment midpoint and the shoulder seam stays straight.',
    'Pocket markers: CPKT1→CPKT4 draft the chest pocket rectangle above the chest line, while SPK1→SPK2 drafts the side pocket opening from the current dart/side-seam reference. All pocket points are selectable in Jacket Free Mode.',
    isDoubleBreasted
      ? `Front button plan: double breasted | top pair at RL1.x - ${Number(jacketButtonPlan.edgeOffset || 0).toFixed(3)}" and RL1.x - 4.000", lower pair at RL1.x - ${Number(jacketButtonPlan.edgeOffset || 0).toFixed(3)}" and RL1.x - 4.000", top single button anchored at RL1.x - 8.000" / RL1.y - 4.500". All DB buttons can be nudged and their buttonholes follow.`
      : `Front button plan: ${normalizeJacketButtonLayout(measurements?.jacketButtonLayout).replaceAll('_', ' ')} | waist button sits on CF3, lower button drop = ${Number(jacketButtonPlan.lowerButtonDistance || 0).toFixed(3)}", upper roll button drop = ${Number(jacketButtonPlan.upperButtonDistance || 0).toFixed(3)}", edge offset = ${Number(jacketButtonPlan.edgeOffset || 0).toFixed(3)}". All rendered buttons can be nudged and their buttonholes follow.` ,
    lapelType === 'diamond_shawl'
      ? 'Diamond shawl draft: the Front cut contour is FHPS→UCOL1→UCOL2→TCOL1→TCOL2, then TCOL2→RL2 (quadratic) and RL2→RL1 (straight). RL2 seeds from RL1 + (3", -1"). FND and LP1 remain internal roll references; no standalone collar outline is emitted.'
      : (lapelType === 'shawl'
        ? 'Shawl draft: the Front cut contour integrates the collar and lapel as FHPS→UCOL1→UCOL2→TCOL1→TCOL2→LP0_M→RL1. FND and LP1 remain internal roll references; no standalone collar outline is emitted.'
        : (lapelType === 'rounded_notch'
        ? 'Collar draft: trace 1.375" from FND along the front neck curve, bridge to UCOL0 on the shoulder seam 0.5" from FHPS toward FLPS, continue in one straight line to UCOL1 by the exact BHPS→CB1 back-neck curve length, then build UCOL2 / TCOL1 / TCOL2 toward the lapel. Rounded Notch adds CP2_M at 0.5" from CP0_M on the straight CP0_M→TCOL2 line and CP1_M at 0.5" from CP0_M on the straight CP0_M→GP_M line. Top Collar Start/End controls govern only TCOL2→CP2_M, while a separate local round-control handle governs CP2_M→CP1_M before the straight tail to GP_M. The reflected fold-over preview uses that same full rounded tail. CP0_M stays internal only.'
        : 'Collar draft: trace 1.375" from FND along the front neck curve, bridge to UCOL0 on the shoulder seam 0.5" from FHPS toward FLPS, continue in one straight line to UCOL1 by the exact BHPS→CB1 back-neck curve length, then build UCOL2 / TCOL1 / TCOL2 toward the lapel. A mirrored 2" fold-over preview of CP0_M→TCOL2 is shown back on the body across the roll line.')),
    ...lapelFormulaLines,
  ];

  const sideSeamChestWaistDelta = Math.abs(dist(FCPS, resolved.FWPS) - dist(BCPS, resolved.BWPS));
  const sideSeamWaistBellyDelta = Math.abs(dist(resolved.FWPS, resolved.FBPS) - dist(resolved.BWPS, resolved.BBPS));
  const sideSeamBellySeatDelta = Math.abs(dist(resolved.FBPS, resolved.FSPS) - dist(resolved.BBPS, resolved.BSPS));

  const frontPanelChestToWaist = quadLength(FCPS, frontChestWaistCtrl, resolved.FWPS);
  const frontPanelWaistToBelly = bellyFishDartEnabled
    ? dist(resolved.FWPS, BD2) + quadLength(BD1, frontWaistBellyCtrl, resolved.FBPS)
    : quadLength(resolved.FWPS, frontWaistBellyCtrl, resolved.FBPS);
  const frontPanelBellyToSeat = quadLength(resolved.FBPS, frontBellySeatCtrl, resolved.FSPS);
  const frontPanelSideSeamTotal = frontPanelChestToWaist + frontPanelWaistToBelly + frontPanelBellyToSeat;

  const sidePanelChestToWaist = quadLength(SCPF, sideFrontChestWaistCtrl, SWPF);
  const sidePanelWaistToBelly = quadLength(SWPF, sideFrontWaistBellyCtrl, SBPF);
  const sidePanelBellyToSeat = quadLength(SBPF, sideFrontBellySeatCtrl, SSPF);
  const sidePanelFrontSeamTotal = sidePanelChestToWaist + sidePanelWaistToBelly + sidePanelBellyToSeat;

  const frontToSideChestWaistDelta = Math.abs(frontPanelChestToWaist - sidePanelChestToWaist);
  const frontToSideWaistBellyDelta = Math.abs(frontPanelWaistToBelly - sidePanelWaistToBelly);
  const frontToSideBellyToSeatDelta = Math.abs(frontPanelBellyToSeat - sidePanelBellyToSeat);
  const frontToSideTotalDelta = Math.abs(frontPanelSideSeamTotal - sidePanelFrontSeamTotal);
  const frontPanelBellyCutGap = bellyFishDartEnabled ? dist(BD1, BD2) : 0;

  const tolerance = 0.0625;
  const validationWarnings = [];
  if (sideSeamChestWaistDelta > tolerance) validationWarnings.push(`Chest→Waist side seam delta exceeds 1/16" (${sideSeamChestWaistDelta.toFixed(3)}").`);
  if (sideSeamWaistBellyDelta > tolerance) validationWarnings.push(`Waist→Belly side seam delta exceeds 1/16" (${sideSeamWaistBellyDelta.toFixed(3)}").`);
  if (sideSeamBellySeatDelta > tolerance) validationWarnings.push(`Belly→Seat side seam delta exceeds 1/16" (${sideSeamBellySeatDelta.toFixed(3)}").`);
  if (frontToSideChestWaistDelta > tolerance) validationWarnings.push(`Front vs side Chest→Waist seam delta exceeds 1/16" (${frontToSideChestWaistDelta.toFixed(3)}").`);
  if (frontToSideWaistBellyDelta > tolerance) validationWarnings.push(`Front vs side Waist→Belly seam delta exceeds 1/16" (${frontToSideWaistBellyDelta.toFixed(3)}").`);
  if (frontToSideBellyToSeatDelta > tolerance) validationWarnings.push(`Front vs side Belly→Seat seam delta exceeds 1/16" (${frontToSideBellyToSeatDelta.toFixed(3)}").`);

  const allPoints = [
    ...namedPoints,
    backNeckCtrl,
    frontNeckCtrl,
    frontChestWaistCtrl,
    frontWaistBellyCtrl,
    frontBellySeatCtrl,
    backChestWaistCtrl,
    backWaistBellyCtrl,
    backBellySeatCtrl,
    sideFrontArmCtrl,
    sideFrontChestWaistCtrl,
    sideFrontWaistBellyCtrl,
    sideFrontBellySeatCtrl,
    sideBackArmCtrl,
    sideBackChestWaistCtrl,
    sideBackWaistBellyCtrl,
    sideBackBellySeatCtrl,
    frontShoulderCtrl,
    backShoulderCtrl,
    ...(!isShawlFamilyLapelType(lapelType) ? [GP, CP0] : []),
    ...(!isShawlFamilyLapelType(lapelType) ? [LB0, LB1] : []),
    LP0,
    LP1,
    ...(!isShawlFamilyLapelType(lapelType) ? [GP_M, CP0_M] : []),
    ...(!isShawlFamilyLapelType(lapelType) ? [LB0_M, LB1_M] : []),
    LP0_M,
    ...(lapelType === 'rounded_notch' ? [RN1, RN2, RN1_M, RN2_M, CP2_M, CP1_M] : []),
    UCOL0,
    UCOL1,
    UCOL2,
    TCOL1,
    TCOL2,
    ...(lapelType === 'shawl' ? [shawlCurveCtrl1, shawlCurveCtrl2, shawlReflectedTcol2, shawlReflectedCtrl1, shawlReflectedCtrl2] : []),
    ...(lapelType === 'diamond_shawl' ? [RL2, diamondShawlCurveCtrl, diamondShawlReflectedTcol2, diamondShawlReflectedRl2, diamondShawlReflectedCtrl, diamondShawlReflectedRl1] : []),
    ...(CPKT1 ? [CPKT1] : []),
    ...(CPKT2 ? [CPKT2] : []),
    ...(CPKT3 ? [CPKT3] : []),
    ...(CPKT4 ? [CPKT4] : []),
    ...(SPK1 ? [SPK1] : []),
    ...(SPK2 ? [SPK2] : []),
  ];

  const minX = Math.min(...allPoints.map((point) => point.x));
  const maxX = Math.max(...allPoints.map((point) => point.x));
  const minY = Math.min(...allPoints.map((point) => point.y));
  const maxY = Math.max(...allPoints.map((point) => point.y));

  const mapPointForCanvas = (point) => ({
    ...point,
    y: Number((-point.y).toFixed(4)),
  });

  const mapLineForCanvas = (line) => ({
    ...line,
    p1: toCanvasPoint(line.p1),
    p2: toCanvasPoint(line.p2),
  });

  const curveHandlesForCanvas = curveHandles.map((handle) => ({
    ...handle,
    point: toCanvasPoint(handle.point),
    segmentStart: toCanvasPoint(handle.segmentStart),
    segmentEnd: toCanvasPoint(handle.segmentEnd),
  }));

  const namedPointsForCanvas = namedPoints.map(mapPointForCanvas);
  const frontMeasurementsForCanvas = frontMeasurements.map(mapLineForCanvas);
  const backMeasurementsForCanvas = backMeasurements.map(mapLineForCanvas);
  const sideMeasurementsForCanvas = sideMeasurements.map(mapLineForCanvas);
  const constructionLinesForCanvas = constructionLines.map(mapLineForCanvas);
  const sideConstructionLinesForCanvas = sideConstructionLines.map(mapLineForCanvas);
  const frontFishDartLinesForCanvas = frontFishDartLines.map(mapLineForCanvas);
  const lapelConstructionLinesForCanvas = lapelConstructionLines.map(mapLineForCanvas);
  const jacketLapelMeasurementLinesForCanvas = jacketLapelMeasurementLines.map(mapLineForCanvas);
  const jacketPeakGuidePointsForCanvas = lapelType === 'peak'
    ? [{ name: 'LP1', ...toCanvasPoint(LP1), piece: 'front' }]
    : [];
  const jacketCollarNotesForCanvas = jacketCollarNotes.map((note) => ({
    ...note,
    point: toCanvasPoint(note.point),
  }));
  const rollLineGuideForCanvas = mapLineForCanvas({ id: 'roll-line-guide', p1: RL0, p2: RL1, label: 'Roll Line RL0→RL1' });

  const frontArmholeCurve = quadLength(resolved.FLPS, frontArmPart1Ctrl, resolved.FXF) + quadLength(resolved.FXF, frontArmPart2Ctrl, FCPS);
  const backArmholeCurve = quadLength(resolved.BLPS, backArmPart1Ctrl, resolved.BXB) + quadLength(resolved.BXB, backArmPart2Ctrl, BAHP);
  const sideArmholeCurve = quadLength(SAHP, sideFrontArmCtrl, SCPF);

  const sideChestDrawn = SCPF.x - SCPB.x;
  const sideWaistDrawn = SWPF.x - SWPB.x;
  const sideBellyDrawn = SBPF.x - SBPB.x;
  const sideSeatDrawn = SSPF.x - SSPB.x;

  // Recompute final drawn widths after all geometry transforms, including Free Mode point deltas.
  const frontChestDrawn = CF2.x - FCPS.x;
  const backChestDrawn = BCPS.x - resolved.CB4.x;
  const frontWaistDrawn = CF3.x - resolved.FWPS.x;
  const backWaistDrawn = resolved.BWPS.x - CB5.x;
  const frontBellyDrawn = CF4.x - resolved.FBPS.x;
  const backBellyDrawn = resolved.BBPS.x - CB6.x;
  const frontSeatDrawn = CF5.x - resolved.FSPS.x;
  const backSeatDrawn = resolved.BSPS.x - CB7.x;

  const frontWaistDrawnNet = frontWaistDrawn - frontWaistDartIntake;
  const frontBellyDartIntake = bellyFishDartEnabled ? dist(BD1, BD2) : 0;
  const chestPocketWidthDrawn = chestPocketEnabled && CPKT1 && CPKT2 ? dist(CPKT1, CPKT2) : 0;
  const chestPocketDepthDrawn = chestPocketEnabled && CPKT2 && CPKT3 ? dist(CPKT2, CPKT3) : 0;
  const sidePocketLengthDrawn = sidePocketEnabled && SPK1 && SPK2 ? dist(SPK1, SPK2) : 0;

  const matchingSummary = {
    chestHalfDrawn: Number((frontChestDrawn + backChestDrawn + sideChestDrawn).toFixed(3)),
    waistHalfDrawn: Number((frontWaistDrawnNet + backWaistDrawn + sideWaistDrawn).toFixed(3)),
    bellyHalfDrawn: Number((frontBellyDrawn + backBellyDrawn + sideBellyDrawn).toFixed(3)),
    seatHalfDrawn: Number((frontSeatDrawn + backSeatDrawn + sideSeatDrawn).toFixed(3)),
    frontLengthDrawn: Number(Math.abs(SP1.y - FHPS.y).toFixed(3)),
    backLengthDrawn: Number(Math.abs(CB7.y - CB1.y).toFixed(3)),
    sideSeamChestWaistDelta: Number(sideSeamChestWaistDelta.toFixed(3)),
    sideSeamWaistBellyDelta: Number(sideSeamWaistBellyDelta.toFixed(3)),
    sideSeamBellySeatDelta: Number(sideSeamBellySeatDelta.toFixed(3)),
    frontPanelChestToWaist: Number(frontPanelChestToWaist.toFixed(3)),
    sidePanelChestToWaist: Number(sidePanelChestToWaist.toFixed(3)),
    frontToSideChestWaistDelta: Number(frontToSideChestWaistDelta.toFixed(3)),
    frontPanelWaistToBelly: Number(frontPanelWaistToBelly.toFixed(3)),
    sidePanelWaistToBelly: Number(sidePanelWaistToBelly.toFixed(3)),
    frontToSideWaistBellyDelta: Number(frontToSideWaistBellyDelta.toFixed(3)),
    frontPanelBellyToSeat: Number(frontPanelBellyToSeat.toFixed(3)),
    sidePanelBellyToSeat: Number(sidePanelBellyToSeat.toFixed(3)),
    frontToSideBellyToSeatDelta: Number(frontToSideBellyToSeatDelta.toFixed(3)),
    frontPanelSideSeamTotal: Number(frontPanelSideSeamTotal.toFixed(3)),
    sidePanelFrontSeamTotal: Number(sidePanelFrontSeamTotal.toFixed(3)),
    frontToSideTotalDelta: Number(frontToSideTotalDelta.toFixed(3)),
    frontPanelBellyCutGap: Number(frontPanelBellyCutGap.toFixed(3)),
    frontWaistDartIntake: Number(frontWaistDartIntake.toFixed(3)),
    frontWaistRaw: Number(frontWaistRaw.toFixed(3)),
    frontWaistNet: Number(frontWaistNet.toFixed(3)),
    frontBellyDartIntake: Number(frontBellyDartIntake.toFixed(3)),
    jacketDartMode,
    chestPocketWidthDrawn: Number(chestPocketWidthDrawn.toFixed(3)),
    chestPocketDepthDrawn: Number(chestPocketDepthDrawn.toFixed(3)),
    sidePocketLengthDrawn: Number(sidePocketLengthDrawn.toFixed(3)),
    chestPocketEnabled,
    sidePocketEnabled,
    jacketButtonLayout: jacketButtonPlan.layout,
    jacketFrontClosure,
    jacketButtonCount: buttons.length,
    jacketButtonholeLength: Number(jacketButtonPlan.buttonholeLength.toFixed(3)),
    jacketLowerButtonDistance: Number(jacketButtonPlan.lowerButtonDistance.toFixed(3)),
    jacketUpperButtonDistance: Number(jacketButtonPlan.upperButtonDistance.toFixed(3)),
    jacketButtonEdgeOffset: Number(jacketButtonPlan.edgeOffset.toFixed(3)),
    backNeckCurveLength: Number(backNeckCurveLength.toFixed(3)),
    frontNeckCollarTraceLength: Number(frontNeckCollarTrace.length.toFixed(3)),
    frontWaistDrawnRaw: Number(frontWaistDrawn.toFixed(3)),
    frontWaistDrawnNet: Number(frontWaistDrawnNet.toFixed(3)),
    frontScyeVertical: Number(Math.abs(FCPS.y - resolved.FLPS.y).toFixed(3)),
    backScyeVertical: Number(Math.abs(BCPS.y - resolved.BLPS.y).toFixed(3)),
    averageScyeVertical: Number((((Math.abs(FCPS.y - resolved.FLPS.y) + Math.abs(BCPS.y - resolved.BLPS.y)) / 2)).toFixed(3)),
    totalArmholeCurve: Number((frontArmholeCurve + backArmholeCurve + sideArmholeCurve).toFixed(3)),
    frontArmholeCurve: Number(frontArmholeCurve.toFixed(3)),
    backArmholeCurve: Number(backArmholeCurve.toFixed(3)),
    sideArmholeCurve: Number(sideArmholeCurve.toFixed(3)),
    frontChestDrawn: Number(frontChestDrawn.toFixed(3)),
    backChestDrawn: Number(backChestDrawn.toFixed(3)),
    sideChestDrawn: Number(sideChestDrawn.toFixed(3)),
    acrossFrontLine: Number((CF1.x - resolved.FXF.x).toFixed(3)),
    acrossBackLine: Number((resolved.BXB.x - CB3Resolved.x).toFixed(3)),
    acrossFrontDrawn: Number((2 * (CF1.x - resolved.FXF.x)).toFixed(3)),
    acrossBackDrawn: Number((2 * (resolved.BXB.x - CB3Resolved.x)).toFixed(3)),
    backShoulderDrawn: Number(quadLength(BHPS, backShoulderCtrl, resolved.BLPS).toFixed(3)),
    frontShoulderDrawn: Number(quadLength(FHPS, frontShoulderCtrl, resolved.FLPS).toFixed(3)),
    autoAcrossFrontDepth: Number(autoAcrossFrontDepth.toFixed(3)),
    resolvedAcrossFrontDepth: Number(resolvedAcrossFrontDepth.toFixed(3)),
    autoAcrossBackDepth: Number(autoAcrossBackDepth.toFixed(3)),
    resolvedAcrossBackDepth: Number(resolvedAcrossBackDepth.toFixed(3)),
    autoFrontNeckWidth: Number(autoFrontNeckWidth.toFixed(3)),
    autoBackNeckWidth: Number(autoBackNeckWidth.toFixed(3)),
    autoFrontNeckDepth: Number(autoFrontNeckDepth.toFixed(3)),
    autoBackNeckDepth: Number(autoBackNeckDepth.toFixed(3)),
    resolvedFrontNeckWidth: Number(frontNeckWidth.toFixed(3)),
    resolvedBackNeckWidth: Number(backNeckWidth.toFixed(3)),
    resolvedFrontNeckDepth: Number(frontNeckDepth.toFixed(3)),
    resolvedBackNeckDepth: Number(backNeckDepth.toFixed(3)),
    formulaVersion: controls.formulaVersion || JACKET_ACTIVE_FORMULA_VERSION,
    geometryVersion: controls.geometryVersion || 'jacket_geom_v0',
  };

  return {
    garmentType: 'jacket',
    frontPath,
    backPath,
    sidePath,
    curveSegments,
    curveHandles: curveHandlesForCanvas,
    pointHandles: pointHandlesResolved,
    namedPoints: namedPointsForCanvas,
    pointReferences,
    constructionLines: constructionLinesForCanvas,
    sideConstructionLines: sideConstructionLinesForCanvas,
    lapelPath: lapelCutOutlinePath,
    lapelConstructionLines: lapelConstructionLinesForCanvas,
    rollLineGuide: rollLineGuideForCanvas,
    lapelType,
    jacketCollarPaths,
    jacketShawlFoldedPreviewPaths,
    jacketCollarNotes: jacketCollarNotesForCanvas,
    frontFishDartLines: frontFishDartLinesForCanvas,
    frontDartPath,
    frontMeasurements: frontMeasurementsForCanvas,
    backMeasurements: backMeasurementsForCanvas,
    sideMeasurements: sideMeasurementsForCanvas,
    jacketPocketMarkers,
    jacketLapelMeasurementLines: jacketLapelMeasurementLinesForCanvas,
    jacketPeakGuidePoints: jacketPeakGuidePointsForCanvas,
    buttons,
    buttonholes,
    matchingSummary,
    lapelSummary: {
      lapelType,
      jacketFrontClosure,
      lapelWidth: Number(lapelWidth.toFixed(3)),
      gorgePointDepth: Number(gorgePointDepth.toFixed(3)),
      gpDistanceFromLp0: isShawlFamilyLapelType(lapelType) ? 0 : Number(dist(LP0, GP).toFixed(3)),
      cp0SegmentLength: isShawlFamilyLapelType(lapelType) ? 0 : Number(dist(GP, CP0).toFixed(3)),
      buttonholeLength: isShawlFamilyLapelType(lapelType) ? 0 : Number(dist(LB0, LB1).toFixed(3)),
      openingWidth: Number((lapelType === 'peak'
        ? dist(FND, LP2)
        : (lapelType === 'diamond_shawl'
          ? dist(TCOL2, RL2)
          : (lapelType === 'shawl'
            ? dist(TCOL2, RL1)
            : (lapelType === 'rounded_notch' ? dist(FND, RN1) : dist(FND, LP0))))).toFixed(3)),
      rollFallLength: Number((lapelType === 'pointed_notch'
        ? pointedNotchRollFallLength
        : (lapelType === 'rounded_notch'
          ? roundedNotchDraft.metrics.fallCurveLength
          : (lapelType === 'diamond_shawl'
            ? diamondShawlLapelDraft.metrics.fallCurveLength
            : (lapelType === 'shawl' ? shawlLapelDraft.metrics.fallCurveLength : peakLapelDraft.metrics.fallCurveLength)))).toFixed(3)),
      crownCurveLength: lapelType === 'diamond_shawl'
        ? diamondShawlLapelDraft.metrics.outerCurveLength
        : shawlLapelDraft.metrics.crownCurveLength,
      fallCurveLength: lapelType === 'diamond_shawl'
        ? diamondShawlLapelDraft.metrics.fallCurveLength
        : shawlLapelDraft.metrics.fallCurveLength,
      shawlUpperStraightLength: lapelType === 'diamond_shawl'
        ? diamondShawlLapelDraft.metrics.upperStraightLength
        : shawlLapelDraft.metrics.upperStraightLength,
      shawlOuterCurveLength: lapelType === 'diamond_shawl'
        ? diamondShawlLapelDraft.metrics.outerCurveLength
        : shawlLapelDraft.metrics.outerCurveLength,
      diamondQuadraticLength: diamondShawlLapelDraft.metrics.diamondQuadraticLength,
      diamondSegmentLength: diamondShawlLapelDraft.metrics.diamondSegmentLength,
      peakRise: peakLapelDraft.metrics.peakRise,
      peakTipDistance: peakLapelDraft.metrics.peakTipDistance,
      backNeckCurveLength: Number(backNeckCurveLength.toFixed(3)),
      collarMatchSegmentLength: Number(collarMatchSegmentLength.toFixed(3)),
      collarMatchSegmentLabel,
      roundedCurveLength: roundedNotchDraft.metrics.roundedCurveLength,
      collarRoundLength: roundedNotchDraft.metrics.collarRoundLength,
      roundDepth: roundedNotchDraft.metrics.roundDepth,
      formulaLines: lapelFormulaLines,
      activeDraft: `${lapelType}_active`,
    },
    validationWarnings,
    freeModeActive,
    pointDeltas: freePointDeltas,
    viewBox: {
      x: minX - 3,
      y: minY - 3,
      width: (maxX - minX) + 6,
      height: (maxY - minY) + 6,
    },
    formulaVersion: controls.formulaVersion || JACKET_ACTIVE_FORMULA_VERSION,
    geometryVersion: controls.geometryVersion || 'jacket_geom_v0',
  };
}
